/* eslint-disable */
// Phase 266 — Sales Module
// ----------------------------------------------------------------------------
// Single-page interface for the Sales domain (Customers, Invoices, Payments,
// Statement). Customers are a thin enrichment view over the existing
// `cleaning_clients` collection (single source of truth — Option C).
// All payments are stored once in `sales_payments` and linked to a Bank
// Account via `bank_account_id` so the same record surfaces in the Bank
// statement without any duplicate document.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { buildPdfUrl, openPdfInBrowser, downloadPdf, getPdfBlobUrl } from '../utils/pdfUtils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { CaretDown } from '@phosphor-icons/react';
import { Plus, Trash, MagnifyingGlass, FileText, Receipt, ChartLineUp, PencilSimple, Envelope, FilePdf, Share, Bell, Printer, CurrencyDollar, DotsThree, Copy, Check, DownloadSimple, X, Prohibit, Lock } from '@phosphor-icons/react';
import { BankAccountCombobox } from '../components/BankAccountCombobox';
import { InvoicePaymentsDialog } from '../components/InvoicePaymentsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (s) => { if (!s) return '-'; const p = s.split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s; };
const fmtMoney = (n) => `€${(Number(n || 0)).toFixed(2)}`;

const TABS = ['customers', 'internal_invoices', 'internal_cash', 'statement'];

// Phase 415 — `Sales` accepts an optional `embed` mode used by the Finance
// section. When embedded, the component:
//   - skips the outer `<Layout>` chrome (Finance owns that),
//   - skips its own title/subtitle and the tab strip (the parent's sidebar
//     drives tab selection),
//   - lets the parent control `activeTab` via props.
// In standalone mode (default — `/sales` route) it behaves exactly as before.
export default function Sales({ embed = false, activeTab: activeTabProp, setActiveTab: setActiveTabProp } = {}) {
  const { t } = useLanguage();
  const { isAdmin } = useAuth();
  const sp = new URLSearchParams(window.location.search);
  const initial = TABS.includes(sp.get('tab')) ? sp.get('tab') : 'customers';
  const [activeTabState, setActiveTabState] = useState(initial);
  // Controlled (Finance-embedded) vs uncontrolled (standalone /sales) mode.
  const activeTab = embed && activeTabProp ? activeTabProp : activeTabState;
  const setActiveTab = embed && setActiveTabProp ? setActiveTabProp : setActiveTabState;
  const [token] = useState(localStorage.getItem('token'));
  const axCfg = { headers: { Authorization: `Bearer ${token}` } };

  // Shared dropdowns
  const [customers, setCustomers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  // Phase 556-fix — `allClients` is consumed by the
  // `clientVendorBuildingOptions` useMemo declared further below. It MUST
  // be initialized before that memo runs, otherwise React throws
  // "Cannot access 'allClients' before initialization" (Temporal Dead
  // Zone) and the entire Finance → Invoices page crashes.
  const [allClients, setAllClients] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  // Phase 548 — Company profile (name, address, phone, email, VAT) for the
  // invoice preview header. Fetched once on mount via /api/company-profile.
  const [companyProfile, setCompanyProfile] = useState({});
  const [search, setSearch] = useState('');

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  // Phase 601 — Track when the open dialog is a CLONE draft. Used to
  // (a) surface a clear DRAFT banner inside the dialog so the user can
  // never mistake an unsaved copy for a persisted record, and
  // (b) show the source invoice number for clarity.
  const [cloneSource, setCloneSource] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    invoiced_to: 'client',
    invoice_no: '',
    // Phase 485 — optional bank reference (e.g. RF-2026-00125) shown
    // in list / preview / PDF / customer statement / customer payments.
    reference_no: '',
    // Phase 518 — Finance separation. official_invoice = default; legacy
    // behaviour. internal_cash_tracking = excluded from Finance/VAT/exports
    // but still tracked in client statements + the dedicated "Internal Cash
    // Tracking" wallet card.
    invoice_type: 'official_invoice',
    client_id: '', building_id: '', date: new Date().toISOString().slice(0, 10),
    due_date: '', terms: '', description: '', amount_net: '', vat_rate: 19, notes: '',
    // Phase 478 — separate customer / internal notes
    customer_notes: '',
    internal_notes: '',
    // Phase 486 — long-form terms & conditions block rendered on PDF
    terms_and_conditions: '',
    // Phase 417 — multi-line items
    items: [{ description: '', quantity: 1, rate: '', vat_rate: 19 }],
    // Phase 431 — discount before VAT, adjustment after VAT
    discount: 0,
    adjustment: 0,
  });
  // Phase 418 — Invoice row actions (Delete confirm + Send-Email dialog)
  const [invoiceDeleteTarget, setInvoiceDeleteTarget] = useState(null);
  const [paymentsDialogInv, setPaymentsDialogInv] = useState(null);
  const [invoiceEmailTarget, setInvoiceEmailTarget] = useState(null);
  const [invoiceEmailForm, setInvoiceEmailForm] = useState({ to: '', cc: '', subject: '', message: '' });
  const [invoiceEmailSending, setInvoiceEmailSending] = useState(false);

  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentDialog, setPaymentDialog] = useState(false);
  // Phase 540 — When set, savePayment will PUT instead of POST and the
  // dialog title switches to "Edit Payment". Cleared when the dialog closes.
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  // Phase 540 — Companion edit-mode flag for Finance Income (Internal
  // Cash) rows. When set, savePayment POSTs to /company/income/{id}/allocate
  // instead of touching sales_payments.
  const [editingIncomeId, setEditingIncomeId] = useState(null);
  // Phase 540 — Capacitor-safe AlertDialog targets (replaces window.confirm
  // which silently no-ops inside the mobile wrapper).
  const [paymentDeleteTarget, setPaymentDeleteTarget] = useState(null);
  const [icDeleteTarget, setIcDeleteTarget] = useState(null);
  // Phase 543 — Void target + reason for bank-linked payments. Voiding
  // preserves the audit trail instead of hard-deleting.
  const [paymentVoidTarget, setPaymentVoidTarget] = useState(null);
  const [paymentVoidReason, setPaymentVoidReason] = useState('');
  const [paymentForm, setPaymentForm] = useState({
    client_id: '', building_id: null, _entity_value: '',
    date: new Date().toISOString().slice(0, 10), amount: '',
    method: 'cash', bank_account_id: '', reference: '', notes: '',
    allocations: [],
  });
  const [paymentInvoices, setPaymentInvoices] = useState([]);

  // Statement state
  const [stmtClientId, setStmtClientId] = useState('');
  const [statement, setStatement] = useState(null);

  // Phase 422 — Zoho-style invoice detail (master-detail layout).
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  // Phase 538 — Always reset the invoice preview when leaving the
  // Invoices/Internal Invoices tab so returning later starts in the
  // default Supplier-style full-width table, never in split-preview mode.
  useEffect(() => {
    if (activeTab !== 'invoices' && activeTab !== 'internal_invoices') {
      setSelectedInvoiceId(null);
    }
  }, [activeTab]);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [invoiceSearch, setInvoiceSearch] = useState('');

  // Phase 438 — Global searchable filters for the standalone Invoices /
  // Payments index views (these tabs now act as cross-customer search
  // indexes accessible from the sidebar). Kept local so they don't bleed
  // into the customer-specific detail tabs.
  // Phase 556 — Centralized Client/Vendor/Building options used by the
  // Invoices, Customer Payments, and Internal Cash filters. Keeps the
  // Buildings entries consistent across the Finance module — if a refactor
  // touches the source data, every filter dropdown updates with it.
  const clientVendorBuildingOptions = React.useMemo(() => {
    const shadows = (allClients || []).filter(c => c.client_type === 'building');
    const shadowByBldg = new Map();
    shadows.forEach(s => { if (s.building_id) shadowByBldg.set(s.building_id, s); });
    const buildingRows = (buildings || []).map(b => {
      const shadow = shadowByBldg.get(b.building_id);
      return {
        value: shadow?.client_id || `bldg:${b.building_id}`,
        name: b.name || shadow?.name || '—',
        building_id: b.building_id,
        shadow_client_id: shadow?.client_id || null,
      };
    });
    shadows.forEach(s => {
      if (s.building_id && !buildings.find(b => b.building_id === s.building_id)) {
        buildingRows.push({
          value: s.client_id,
          name: s.name || '—',
          building_id: s.building_id,
          shadow_client_id: s.client_id,
        });
      }
    });
    buildingRows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    // Phase 556-fix — Map dropdown-value -> building_id so the matcher
    // can resolve building filters whether the invoice/payment row stores
    // building_id, client_id (shadow), or both. Without this lookup the
    // matcher only checked row.client_id, dropping the 61+ historical
    // invoices that carry building_id but no client_id.
    const buildingIdByValue = new Map();
    buildingRows.forEach(r => buildingIdByValue.set(r.value, r.building_id));
    return { clients: customers || [], buildings: buildingRows, buildingIdByValue };
  }, [customers, buildings, allClients]);

  // Phase 556-fix — Matches a row.client_id / row.building_id against a
  // dropdown value emitted by the helper. Buildings are matched by
  // building_id regardless of whether the dropdown value is the shadow
  // client_id or the "bldg:<id>" sentinel for orphan buildings. Falls
  // back to the row's client_id for pure client/vendor selections.
  const matchesClientVendorBuilding = (row, selected) => {
    if (!selected || selected === '__all__') return true;
    if (selected.startsWith('bldg:')) {
      const bid = selected.slice(5);
      return (row.building_id === bid) || (row.client_id === bid);
    }
    const bid = clientVendorBuildingOptions.buildingIdByValue.get(selected);
    if (bid) {
      // Selected value is a building's shadow client — match rows that
      // either reference the shadow client_id OR the underlying building.
      return row.client_id === selected || row.building_id === bid;
    }
    return row.client_id === selected;
  };

  const [invFilterCustomer, setInvFilterCustomer] = useState('__all__');
  const [invFilterStatus, setInvFilterStatus] = useState('__all__');
  // Phase 520 — Invoice type segmented filter: 'all' | 'official' | 'internal_cash'.
  // Default `all` shows both so the user can still see everything at a glance.
  // The toggle is rendered as a 3-button segmented control above the table.
  // Phase 524 — Internal Invoices moved to a dedicated tab; the segmented
  // Type filter was removed. State kept here for back-compat in case any
  // existing test refers to it, but is no longer wired into the UI.
  const [invFilterType, setInvFilterType] = useState('all'); // eslint-disable-line no-unused-vars
  const [invFilterDateFrom, setInvFilterDateFrom] = useState('');
  const [invFilterDateTo, setInvFilterDateTo] = useState('');
  const [invFilterAmountMin, setInvFilterAmountMin] = useState('');
  const [invFilterAmountMax, setInvFilterAmountMax] = useState('');
  const [payFilterCustomer, setPayFilterCustomer] = useState('__all__');
  // Phase 553 — Status filter (All / Paid / Partial / Unpaid).
  const [payFilterStatus, setPayFilterStatus] = useState('__all__');
  const [payFilterMethod, setPayFilterMethod] = useState('__all__');
  const [payFilterRef, setPayFilterRef] = useState('');
  const [payFilterDateFrom, setPayFilterDateFrom] = useState('');
  const [payFilterDateTo, setPayFilterDateTo] = useState('');
  const [payFilterAmountMin, setPayFilterAmountMin] = useState('');
  const [payFilterAmountMax, setPayFilterAmountMax] = useState('');

  // Phase 436 — Nested Customer detail: clicking a customer in the table
  // opens an in-page detail view (Summary / Invoices / Payments / Statement
  // tabs) instead of routing to separate sidebar entries.
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [customerSubTab, setCustomerSubTab] = useState('summary');

  // Customer edit dialog (TIN, billing_address, email)
  const [customerDialog, setCustomerDialog] = useState(false);
  const [customerForm, setCustomerForm] = useState({ client_id: '', name: '', tin: '', billing_address: '', email: '' });

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API}/sales/customers`, axCfg);
      setCustomers(res.data || []);
    } catch (e) { toast.error('Failed to load customers'); }
  };
  const fetchBuildings = async () => {
    try {
      const res = await axios.get(`${API}/buildings`, axCfg);
      setBuildings(res.data || []);
    } catch { /* ignore */ }
  };
  const fetchBankAccounts = async () => {
    try {
      const res = await axios.get(`${API}/bank-accounts`, axCfg);
      setBankAccounts(res.data || []);
    } catch { /* ignore */ }
  };
  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/sales/invoices`, axCfg);
      setInvoices(res.data || []);
    } catch (e) { toast.error('Failed to load invoices'); }
  };
  const fetchPayments = async () => {
    try {
      const res = await axios.get(`${API}/sales/payments`, axCfg);
      setPayments(res.data || []);
    } catch (e) { toast.error('Failed to load payments'); }
  };
  // Phase 528-fix — Finance Income is the actual home of every existing
  // Internal Cash entry created via Finance → Income (sync_to_cleaning
  // also surfaces them in Cleaning Management). The Internal Cash view
  // must merge them with sales_payments to show what users already
  // recorded.
  const [financeIncome, setFinanceIncome] = useState([]);
  const fetchFinanceIncome = async () => {
    try {
      const res = await axios.get(`${API}/company/income`, axCfg);
      setFinanceIncome(res.data || []);
    } catch { /* ignore — only used by Internal Cash view */ }
  };
  // Phase 533 — Building-type clients are excluded from /sales/customers
  // but they CAN own Internal Cash payments (e.g. building cleaning
  // revenue). Pull the full cleaning_clients list so the Internal Cash
  // view can resolve those names instead of showing "—".
  // Phase 556-fix — `allClients` useState moved to the top of the
  // component (next to `customers`) so it exists before the
  // `clientVendorBuildingOptions` useMemo. Only the fetcher remains here.
  const fetchAllClients = async () => {
    try {
      const res = await axios.get(`${API}/cleaning/clients`, axCfg);
      setAllClients(res.data || []);
    } catch { /* best-effort */ }
  };
  const fetchStatement = async (clientId) => {
    if (!clientId) { setStatement(null); return; }
    // Phase 557 — Building selections come from the unified Customers list
    // as "bldg:<building_id>". Route them to the dedicated building
    // statement endpoint so historical invoices/payments linked by
    // building_id (with null client_id) surface correctly.
    try {
      const url = clientId.startsWith('bldg:')
        ? `${API}/sales/buildings/${clientId.slice(5)}/statement`
        : `${API}/sales/customers/${clientId}/statement`;
      const res = await axios.get(url, axCfg);
      setStatement(res.data);
    } catch (e) { toast.error('Failed to load statement'); }
  };

  useEffect(() => { fetchCustomers(); fetchBuildings(); fetchBankAccounts(); fetchInvoices(); fetchPayments(); fetchFinanceIncome(); fetchAllClients(); fetchCompanyProfile(); }, []);
  const fetchCompanyProfile = async () => {
    try {
      const res = await axios.get(`${API}/company-profile`, axCfg);
      setCompanyProfile(res.data || {});
    } catch { /* non-fatal */ }
  };
  useEffect(() => { fetchStatement(stmtClientId); }, [stmtClientId]); // eslint-disable-line

  // Phase 436 — when a customer is opened in the nested detail view,
  // automatically fetch their statement so the Statement tab is ready.
  useEffect(() => {
    if (selectedCustomerId) {
      fetchStatement(selectedCustomerId);
      setStmtClientId(selectedCustomerId);
    }
  }, [selectedCustomerId]); // eslint-disable-line

  // Persist tab in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.set('tab', activeTab);
    window.history.replaceState(null, '', `${window.location.pathname}?${params}`);
  }, [activeTab]);

  // Phase 605 — Honor an inbound `?search=` query param coming from
  // Cleaning Management's "Open in Finance" deep-link. We prefill the
  // Invoices search filter so the target invoice surfaces immediately
  // and the URL is then cleared so a manual edit doesn't fight the
  // user's typing.
  useEffect(() => {
    if (activeTab !== 'invoices' && activeTab !== 'internal_invoices') return;
    const params = new URLSearchParams(window.location.search);
    const incoming = params.get('search');
    if (incoming) {
      setInvoiceSearch(incoming);
      params.delete('search');
      const qs = params.toString();
      window.history.replaceState(null, '', `${window.location.pathname}${qs ? '?' + qs : ''}`);
    }
  }, [activeTab]);

  // ---------- Customer save ----------
  const openCustomerEdit = (c) => {
    setCustomerForm({
      client_id: c.client_id, name: c.name, tin: c.tin || '',
      billing_address: c.billing_address || '', email: c.email || '',
    });
    setCustomerDialog(true);
  };
  const saveCustomer = async () => {
    try {
      await axios.put(`${API}/cleaning/clients/${customerForm.client_id}`, {
        tin: customerForm.tin, billing_address: customerForm.billing_address, email: customerForm.email,
      }, axCfg);
      toast.success(t('saved'));
      setCustomerDialog(false);
      fetchCustomers();
    } catch (e) {
      // Phase 547 — Safe-render structured 409 errors (duplicate name etc.).
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || e?.response?.data?.message || 'Error');
      toast.error(msg);
    }
  };

  // ---------- Invoice save / delete ----------
  const openInvoiceCreate = (defaultType = 'official_invoice') => {
    setEditingInvoice(null);
    setInvoiceForm({
      invoiced_to: 'client',
      invoice_no: '',
      reference_no: '',
      invoice_type: defaultType,
      client_id: '', building_id: '', date: new Date().toISOString().slice(0, 10),
      due_date: '', terms: '', description: '', amount_net: '', vat_rate: 19, notes: '',
      customer_notes: '',
      internal_notes: '',
      terms_and_conditions: '',
      items: [{ description: '', quantity: 1, rate: '', vat_rate: 19 }],
      discount: 0,
      adjustment: 0,
    });
    setInvoiceDialog(true);
  };
  // Phase 418 — Edit: load existing items into the form so they can be tweaked
  const openInvoiceEdit = (inv) => {
    const items = (inv.items && inv.items.length > 0)
      ? inv.items.map(it => ({
          description: it.description || '',
          quantity: it.quantity ?? 1,
          rate: it.rate ?? '',
          vat_rate: it.vat_rate ?? 19,
        }))
      : [{
          description: inv.description || '',
          quantity: 1,
          rate: inv.amount_net ?? '',
          vat_rate: inv.vat_rate ?? 19,
        }];
    setEditingInvoice(inv);
    setInvoiceForm({
      invoiced_to: inv.building_id ? 'building' : 'client',
      invoice_no: inv.invoice_no || '',
      reference_no: inv.reference_no || '',
      // Phase 518 — preserve the existing invoice_type when editing.
      invoice_type: inv.invoice_type || 'official_invoice',
      client_id: inv.client_id || '',
      building_id: inv.building_id || '',
      date: inv.date || new Date().toISOString().slice(0, 10),
      due_date: inv.due_date || '',
      terms: inv.terms || '',
      description: inv.description || '',
      amount_net: '',
      vat_rate: inv.vat_rate ?? 19,
      notes: inv.notes || '',
      customer_notes: inv.customer_notes || inv.notes || '',
      internal_notes: inv.internal_notes || '',
      terms_and_conditions: inv.terms_and_conditions || '',
      items,
      discount: inv.discount ?? 0,
      adjustment: inv.adjustment ?? 0,
    });
    setInvoiceDialog(true);
  };
  const saveInvoice = async () => {
    if (invoiceForm.invoiced_to === 'client' && !invoiceForm.client_id) {
      toast.error(t('selectClient') || 'Select a client'); return;
    }
    if (invoiceForm.invoiced_to === 'building' && !invoiceForm.building_id) {
      toast.error(t('selectBuilding') || 'Select a building'); return;
    }
    const cleanItems = (invoiceForm.items || [])
      .filter(it => (it.description || '').trim() || parseFloat(it.rate) > 0)
      .map(it => ({
        description: (it.description || '').trim(),
        quantity: parseFloat(it.quantity) || 1,
        rate: parseFloat(it.rate) || 0,
        vat_rate: parseFloat(it.vat_rate) || 0,
      }));
    const subtotal = cleanItems.reduce((s, it) => s + it.quantity * it.rate, 0);
    if (!cleanItems.length || subtotal <= 0) { toast.error('Add at least one invoice item with a rate'); return; }
    try {
      const payload = {
        client_id: invoiceForm.invoiced_to === 'client' ? invoiceForm.client_id : null,
        building_id: invoiceForm.invoiced_to === 'building' ? invoiceForm.building_id : null,
        invoice_no: (invoiceForm.invoice_no || '').trim() || undefined,
        reference_no: (invoiceForm.reference_no || '').trim() || undefined,
        // Phase 518 — Finance separation classifier (official_invoice |
        // internal_cash_tracking).
        invoice_type: invoiceForm.invoice_type || 'official_invoice',
        date: invoiceForm.date,
        due_date: invoiceForm.due_date || null,
        terms: invoiceForm.terms || '',
        // Phase 735.50 — Bug fix: There is no top-level description input
        // in the invoice dialog; the user only edits per-line item
        // descriptions. Previously we kept `invoiceForm.description` as a
        // legacy override, but on edit it was pre-loaded with the OLD
        // joined value and never refreshed, so saving carried the stale
        // description into the DB and the invoices list kept showing the
        // outdated text. Always derive `description` from the current
        // line items so create and edit stay in sync.
        description: cleanItems.map(i => i.description).filter(Boolean).join(' / '),
        items: cleanItems,
        discount: parseFloat(invoiceForm.discount) || 0,
        adjustment: parseFloat(invoiceForm.adjustment) || 0,
        notes: invoiceForm.notes,
        customer_notes: invoiceForm.customer_notes,
        internal_notes: invoiceForm.internal_notes,
        terms_and_conditions: invoiceForm.terms_and_conditions,
      };
      if (editingInvoice) await axios.put(`${API}/sales/invoices/${editingInvoice.invoice_id}`, payload, axCfg);
      else await axios.post(`${API}/sales/invoices`, payload, axCfg);
      toast.success(t('saved'));
      setInvoiceDialog(false);
      // Phase 601 — Clone draft is now persisted, release the source ref.
      setCloneSource(null);
      fetchInvoices(); fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  const deleteInvoice = async (inv) => {
    try {
      await axios.delete(`${API}/sales/invoices/${inv.invoice_id}`, axCfg);
      toast.success(t('deleted') || 'Deleted');
      setInvoiceDeleteTarget(null);
      fetchInvoices(); fetchCustomers();
    } catch (e) {
      // Phase 1026g — If the backend reports "partially-paid", first try
      // the auto-repair recompute endpoint (reconciles paid_amount against
      // ACTIVE payment allocations; cleans up stale state from historic
      // deleted payments) and retry the delete once.
      const detail = e.response?.data?.detail || '';
      if (/INVOICE_HAS_PAYMENTS/.test(detail)) {
        toast.error(detail.replace('INVOICE_HAS_PAYMENTS: ', ''));
        setInvoiceDeleteTarget(null);
        setPaymentsDialogInv(inv);
        fetchInvoices();
        return;
      }
      if (/partially-paid/i.test(detail)) {
        try {
          const rc = await axios.post(`${API}/sales/invoices/${inv.invoice_id}/recompute-paid`, {}, axCfg);
          if (rc.data?.changed && (rc.data?.active_paid || 0) <= 0.01) {
            await axios.delete(`${API}/sales/invoices/${inv.invoice_id}`, axCfg);
            toast.success(t('deletedAfterRepair') || 'Invoice repaired and deleted');
            setInvoiceDeleteTarget(null);
            fetchInvoices(); fetchCustomers();
            return;
          }
          if (rc.data?.changed) {
            toast.error(`${t('invoiceStillHasActivePayment') || 'Invoice still has active payments'}: €${(rc.data?.active_paid || 0).toFixed(2)}. ${t('reverseFirst') || 'Reverse them first.'}`);
            fetchInvoices();
            return;
          }
        } catch (_) { /* fall through to the original error */ }
      }
      toast.error(detail || 'Error');
    }
  };

  // Phase 418 — View PDF: open the invoice PDF in a new tab. Token is in the
  // header for axios calls, but `window.open` won't carry it; the endpoint
  // accepts a `?token=` query param via the auth middleware too. We fall back
  // to a fetch + blob URL so the token can travel via Authorization header.
  // Phase 520 — Inline PDF preview state (replaces popup-prone window.open).
  // Same pattern as BuildingEmailsTab (Phase 511): point the iframe at a
  // direct authenticated URL with `?token=...&disposition=inline` so the
  // browser PDF viewer renders the document natively. No blob, no popup,
  // no popup-blocker rejection, no Capacitor webview blank-screen bug.
  const [invoicePdfUrl, setInvoicePdfUrl] = useState('');
  const [invoicePdfTitle, setInvoicePdfTitle] = useState('');
  const [invoicePdfOpen, setInvoicePdfOpen] = useState(false);
  // Phase 727 — Fetch the PDF as a Blob and embed via `blob:` URL. This
  // bypasses Chrome's blocking of Cloudflare bot-challenge / third-party
  // cookie interstitials that intermittently break authenticated iframe
  // src loads with "This page has been blocked by Chrome".
  const [invoicePdfBlobUrl, setInvoicePdfBlobUrl] = useState('');
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const [invoicePdfError, setInvoicePdfError] = useState('');

  useEffect(() => {
    if (!invoicePdfOpen || !invoicePdfUrl) return;
    let cancelled = false;
    let currentBlobUrl = '';
    setInvoicePdfLoading(true);
    setInvoicePdfError('');
    (async () => {
      try {
        // Phase 735.13 — Centralized blob-URL pipeline (`pdfUtils.getPdfBlobUrl`).
        // Uses cookie auth, strips any legacy `?token=` param, and never
        // constructs a fresh `?token=` URL — the single source of truth for
        // every PDF preview in the app.
        currentBlobUrl = await getPdfBlobUrl(invoicePdfUrl, {});
        if (cancelled) {
          try { URL.revokeObjectURL(currentBlobUrl); } catch { /* noop */ }
          return;
        }
        setInvoicePdfBlobUrl(currentBlobUrl);
      } catch (err) {
        if (!cancelled) {
          const msg = err && err.message ? err.message : String(err);
          setInvoicePdfError(
            /Failed to fetch|NetworkError|blocked/i.test(msg)
              ? 'The request was blocked (likely a browser extension such as uBlock Origin or AdGuard). Disable the extension for this site or open the PDF in a new tab.'
              : msg
          );
        }
      } finally {
        if (!cancelled) setInvoicePdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (currentBlobUrl) URL.revokeObjectURL(currentBlobUrl);
    };
  }, [invoicePdfOpen, invoicePdfUrl]);

  useEffect(() => {
    // Revoke blob when the previous one is replaced.
    return () => {
      if (invoicePdfBlobUrl) URL.revokeObjectURL(invoicePdfBlobUrl);
    };
  }, [invoicePdfBlobUrl]);

  // Phase 726 — "Export PDF" now first opens a column-picker dialog. The
  // selected set is persisted in localStorage per browser so future exports
  // remember the user's preference.
  const ALL_PDF_COLS = [
    { key: 'date',        label: t('date')       || 'Date' },
    { key: 'invoice_no',  label: t('invoiceNumber') || 'Invoice Number' },
    { key: 'client',      label: t('clientVendor') || 'Client / Vendor' },
    { key: 'description', label: t('description') || 'Description' },
    { key: 'reference',   label: (t('reference') || 'Reference') + ' #' },
    { key: 'net',         label: t('netAmount') || 'Net Amount' },
    { key: 'vat',         label: t('vatAmount') || 'VAT' },
    { key: 'total',       label: t('totalAmount') || 'Total' },
    { key: 'paid',        label: t('paid') || 'Paid' },
    { key: 'status',      label: t('status') || 'Status' },
  ];
  const PDF_COLS_STORAGE_KEY = 'sales-invoice-pdf-cols-v1';
  const loadSavedCols = () => {
    try {
      const raw = localStorage.getItem(PDF_COLS_STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr) && arr.length > 0) {
          const valid = ALL_PDF_COLS.map(c => c.key);
          const filtered = arr.filter(k => valid.includes(k));
          if (filtered.length > 0) return filtered;
        }
      }
    } catch (_) { /* ignore */ }
    return ALL_PDF_COLS.map(c => c.key);
  };
  const [pdfColsOpen, setPdfColsOpen] = useState(false);
  const [pdfCols, setPdfCols] = useState(loadSavedCols);
  const [pendingPdfBuild, setPendingPdfBuild] = useState(null); // fn to run when user confirms

  const togglePdfCol = (key) => {
    setPdfCols(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };
  const handleConfirmPdfCols = () => {
    if (pdfCols.length === 0) return; // guarded by button disable too
    try { localStorage.setItem(PDF_COLS_STORAGE_KEY, JSON.stringify(pdfCols)); } catch (_) { /* ignore */ }
    setPdfColsOpen(false);
    if (pendingPdfBuild) {
      pendingPdfBuild(pdfCols);
      setPendingPdfBuild(null);
    }
  };

  const viewInvoicePdf = (inv) => {
    const url = buildPdfUrl(`/sales/invoices/${inv.invoice_id}/pdf`, { disposition: 'inline' });
    setInvoicePdfUrl(url);
    setInvoicePdfTitle(`${inv.invoice_no || (t('invoice') || 'Invoice')} — ${inv.customer_name || inv.client_name || inv.building_name || ''}`);
    setInvoicePdfOpen(true);
  };

  // Phase 418 — Open the Send-Email dialog pre-filled with the customer's
  // email and a sensible subject/body.
  const openInvoiceEmail = (inv) => {
    const client = customers.find(c => c.client_id === inv.client_id);
    setInvoiceEmailTarget(inv);
    setInvoiceEmailForm({
      to: client?.email || '',
      cc: '',
      subject: `Invoice ${inv.invoice_no} — ${fmtMoney(inv.total)}`,
      message: `Dear ${client?.name || 'Customer'},\n\nPlease find attached invoice ${inv.invoice_no} dated ${fmtDate(inv.date)} for a total of ${fmtMoney(inv.total)}.\n\nThank you.`,
    });
  };
  const sendInvoiceEmail = async () => {
    if (!invoiceEmailTarget) return;
    if (!invoiceEmailForm.to.trim()) { toast.error(t('emailRequired') || 'Recipient email is required'); return; }
    setInvoiceEmailSending(true);
    try {
      await axios.post(`${API}/send-document-email`, {
        to_email: invoiceEmailForm.to.trim(),
        cc: invoiceEmailForm.cc.trim(),
        subject: invoiceEmailForm.subject,
        message: invoiceEmailForm.message,
        pdf_url: `/api/sales/invoices/${invoiceEmailTarget.invoice_id}/pdf`,
        filename: `Invoice_${invoiceEmailTarget.invoice_no || invoiceEmailTarget.invoice_id}.pdf`,
      }, axCfg);
      toast.success(t('emailSent') || 'Email sent');
      setInvoiceEmailTarget(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setInvoiceEmailSending(false); }
  };

  // Phase 422 — Zoho-style actions
  // Phase 599 — Clone now copies EVERY form-relevant field from the
  // source invoice (including the critical `invoiced_to` selector,
  // `invoice_type` for Internal vs Official, discount/adjustment, all
  // notes blocks). Without these the save handler used to silently
  // send null client_id + null building_id, the backend returned 400,
  // but a prior bug short-circuited validation and still showed the
  // success toast. New behaviour: clone produces a real, fully-formed
  // draft that the user can save in one click.
  const cloneInvoice = (inv) => {
    setEditingInvoice(null);
    setCloneSource(inv);
    const items = (inv.items && inv.items.length > 0)
      ? inv.items.map(it => ({ description: it.description || '', quantity: it.quantity ?? 1, rate: it.rate ?? '', vat_rate: it.vat_rate ?? 19 }))
      : [{ description: inv.description || '', quantity: 1, rate: inv.amount_net ?? '', vat_rate: inv.vat_rate ?? 19 }];
    setInvoiceForm({
      // Carry over recipient + type so the backend accepts the POST.
      invoiced_to: inv.building_id ? 'building' : 'client',
      client_id: inv.client_id || '',
      building_id: inv.building_id || '',
      invoice_type: inv.invoice_type || 'official_invoice',
      // Blank invoice_no → backend auto-generates a NEW number.
      invoice_no: '',
      reference_no: inv.reference_no || '',
      // Today's date for the clone; user can adjust if needed.
      date: new Date().toISOString().slice(0, 10),
      due_date: '',
      terms: inv.terms || '',
      description: inv.description || '',
      amount_net: '',
      vat_rate: inv.vat_rate ?? 19,
      discount: inv.discount ?? 0,
      adjustment: inv.adjustment ?? 0,
      notes: inv.notes || '',
      customer_notes: inv.customer_notes || '',
      internal_notes: inv.internal_notes || '',
      terms_and_conditions: inv.terms_and_conditions || '',
      items,
    });
    setInvoiceDialog(true);
    setMoreMenuOpen(false);
    toast.info(t('clonedInvoice') || 'Invoice cloned — review and save.');
  };
  // Download the PDF as a file instead of opening it.
  const downloadInvoicePdf = async (inv) => {
    try {
      await downloadPdf(
        `/sales/invoices/${inv.invoice_id}/pdf`,
        { disposition: 'download' },
        `Invoice_${inv.invoice_no || inv.invoice_id}.pdf`,
      );
    } catch (e) { toast.error(e?.message || 'Failed to download PDF'); }
  };
  // Open the PDF + immediately trigger the print dialog.
  // Phase 520 — Both the row-level "View PDF" button AND the inline
  // preview's "PDF / Print" button now route through the same inline
  // modal. The browser's PDF toolbar already provides Print + Download,
  // so we no longer need the popup-based print flow that breaks under
  // popup blockers and Capacitor.
  const printInvoicePdf = (inv) => viewInvoicePdf(inv);
  // Mark-as-sent simply opens the email dialog (a "send" is the real action).
  const markInvoiceAsSent = (inv) => {
    toast.info(t('markAsSentHint') || 'Open the Send by Email dialog to send this invoice.');
    openInvoiceEmail(inv);
    setMoreMenuOpen(false);
  };
  // "Reminders" placeholder — for now opens the Send-Email dialog with a
  // reminder-flavoured default body.
  const sendReminder = (inv) => {
    const client = customers.find(c => c.client_id === inv.client_id);
    setInvoiceEmailTarget(inv);
    setInvoiceEmailForm({
      to: client?.email || '',
      cc: '',
      subject: `Reminder — Invoice ${inv.invoice_no} (${fmtMoney(inv.total)})`,
      message: `Dear ${client?.name || 'Customer'},\n\nThis is a friendly reminder that invoice ${inv.invoice_no} dated ${fmtDate(inv.date)} for a total of ${fmtMoney(inv.total)} is still outstanding.\n\nKindly arrange payment at your earliest convenience.\n\nThank you.`,
    });
  };
  // "Share" — copies a shareable link to the clipboard.
  const shareInvoiceLink = async (inv) => {
    const url = `${window.location.origin}/api/sales/invoices/${inv.invoice_id}/pdf`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t('linkCopied') || 'PDF link copied to clipboard');
    } catch {
      toast.info(url);
    }
  };
  // "Record Payment" — prefills the payment dialog for this invoice's client.
  const recordPaymentForInvoice = (inv) => {
    openPaymentCreate(inv.client_id);
  };

  // ---------- Payment save / delete ----------
  // Phase 540 — Internal Cash row delete + edit handlers.
  const deleteInternalCashRow = async (r) => {
    try {
      if (r.kind === 'sales_payment') {
        await axios.delete(`${API}/sales/payments/${r.row_id}`, axCfg);
      } else {
        await axios.delete(`${API}/company/income/${r.row_id}`, axCfg);
      }
      toast.success(t('deleted') || 'Deleted');
      fetchPayments();
      fetchFinanceIncome();
      fetchInvoices();
    } catch { toast.error(t('errorOccurred') || 'Failed to delete'); }
  };
  const editInternalCashRow = (r) => {
    if (r.kind === 'sales_payment') {
      const p = payments.find(x => x.sales_payment_id === r.row_id);
      if (!p) return;
      openEditPayment(p);
    } else if (r.kind === 'finance_income') {
      // Phase 540 — Finance Income (Internal Cash) entries support
      // allocations via /api/company/income/{id}/allocate. Open the same
      // dialog with a different save target so the user has a unified UX.
      const e = (financeIncome || []).find(x => x.income_id === r.row_id);
      if (!e) return;
      setEditingPaymentId(null);
      setEditingIncomeId(e.income_id);
      setPaymentForm({
        client_id: e.client_id || '',
        building_id: e.building_id || null,
        _entity_value: e.building_id ? `bldg:${e.building_id}` : (e.client_id || ''),
        date: e.date,
        amount: String(e.amount),
        method: e.source || 'cash',
        bank_account_id: '',
        reference: e.description || e.income_id,
        notes: e.notes || '',
        payment_account: 'internal_cash_tracking',
        allocations: Array.isArray(e.invoice_allocations) ? e.invoice_allocations : [],
      });
      loadInvoicesForClient(e.client_id || '', e.building_id || null);
      setPaymentDialog(true);
    }
  };
  // Phase 566 — Translate a (client_id, building_id) pair into the
  // dropdown value used by the unified Client/Vendor/Building selector
  // (so that the existing entity is highlighted when the payment dialog
  // opens in edit mode).
  const resolveEntityValue = (clientId, buildingId) => {
    if (buildingId) {
      const opt = (clientVendorBuildingOptions.buildings || []).find(b => b.building_id === buildingId);
      if (opt) return opt.value;
      return `bldg:${buildingId}`;
    }
    return clientId || '';
  };

  // Phase 553 — Reused by both the Internal Cash row click and the
  // Customer Payments row click. Opens the unified Payment dialog in
  // edit mode for any existing `sales_payment` (official OR internal).
  // Forwards `building_id` so the allocation dialog includes building
  // invoices even when no past-payment link exists yet.
  const openEditPayment = (p) => {
    if (!p) return;
    setEditingPaymentId(p.sales_payment_id);
    setEditingIncomeId(null);
    setPaymentForm({
      client_id: p.client_id,
      building_id: p.building_id || null,
      _entity_value: resolveEntityValue(p.client_id, p.building_id || null),
      date: p.date, amount: String(p.amount),
      method: p.method || 'cash', bank_account_id: p.bank_account_id || '',
      reference: p.reference || '', notes: p.notes || '',
      payment_account: p.payment_account || 'company_cash',
      allocations: Array.isArray(p.allocations) ? p.allocations : [],
    });
    loadInvoicesForClient(p.client_id, p.building_id || null);
    setPaymentDialog(true);
  };
  // Phase 619 — Finance-origin bank receipts (rows surfaced into
  // sales/payments from `company_income` with source='finance') are NOT
  // editable as sales_payments (they have no `sales_payment_id`). But
  // the user MUST be able to attach/reattach invoice allocations to
  // them. Open the same dialog with `editingIncomeId` set so save flows
  // through `/company/income/{id}/allocate`; amount/bank/date are
  // disabled (bank transaction protected) but the allocation table is
  // fully interactive.
  const openEditFinancePayment = async (p) => {
    if (!p || !p.income_id) return;
    // Try the in-memory list first.
    let inc = (financeIncome || []).find(x => x.income_id === p.income_id);
    if (!inc) {
      try {
        const res = await axios.get(`${API}/company/income`, axCfg);
        setFinanceIncome(res.data || []);
        inc = (res.data || []).find(x => x.income_id === p.income_id);
      } catch { /* fall through */ }
    }
    if (!inc) {
      // Fall back to the row's own data when the lookup fails — the
      // allocate endpoint only needs income_id + allocations.
      inc = {
        income_id: p.income_id,
        client_id: p.client_id || '',
        building_id: p.building_id || null,
        date: p.date,
        amount: p.amount,
        source: p.method || 'bank',
        bank_account_id: p.bank_account_id || '',
        description: p.reference || '',
        notes: p.notes || '',
        affects_treasury: true,
        invoice_allocations: [],
      };
    }
    setEditingPaymentId(null);
    setEditingIncomeId(inc.income_id);
    // For treasury-affecting incomes (real bank receipts) the dialog
    // should expose Official invoices, not Internal Cash. Match the
    // backend's strict separation rule.
    const paymentAccount = (inc.affects_treasury === false)
      ? 'internal_cash_tracking'
      : 'company_cash';
    setPaymentForm({
      client_id: inc.client_id || '',
      building_id: inc.building_id || null,
      _entity_value: resolveEntityValue(inc.client_id || '', inc.building_id || null),
      date: inc.date,
      amount: String(inc.amount),
      method: inc.source || 'bank',
      bank_account_id: inc.bank_account_id || '',
      reference: inc.description || inc.income_id,
      notes: inc.notes || '',
      payment_account: paymentAccount,
      allocations: Array.isArray(inc.invoice_allocations) ? inc.invoice_allocations : [],
    });
    loadInvoicesForClient(inc.client_id || '', inc.building_id || null);
    setPaymentDialog(true);
  };
  const openPaymentCreate = async (clientId = '', defaultAccount = 'company_cash') => {
    setEditingPaymentId(null);
    setEditingIncomeId(null);
    // Phase 553 — Auto-fetch the next Payment Number so the dialog
    // pre-fills the reference field with PAY-1001 etc. Falls back to
    // empty if the endpoint is unreachable; backend will auto-gen on
    // submit either way.
    let nextNumber = '';
    try {
      const res = await axios.get(`${API}/sales/payments/next-number`, axCfg);
      nextNumber = res.data?.next || '';
    } catch { /* non-fatal */ }
    setPaymentForm({
      client_id: clientId,
      building_id: null,
      _entity_value: clientId || '',
      date: new Date().toISOString().slice(0, 10), amount: '',
      method: 'cash', bank_account_id: '', reference: nextNumber, notes: '',
      // Phase 526 — `payment_account` routes the cash into either the
      // official treasury (company_cash/company_bank) or the dedicated
      // Internal Cash wallet (internal_cash_tracking).
      payment_account: defaultAccount,
      allocations: [],
    });
    setPaymentInvoices([]);
    setPaymentDialog(true);
  };
  const loadInvoicesForClient = async (clientId, buildingId = null) => {
    if (!clientId && !buildingId) { setPaymentInvoices([]); return; }
    try {
      // Phase 553 — Pass building_id alongside client_id so the
      // allocation dialog ALWAYS surfaces invoices issued directly to
      // the linked building, in addition to the client's own invoices
      // and the Phase 552 past-payment-linked buildings.
      const params = new URLSearchParams();
      if (clientId) params.set('client_id', clientId);
      if (buildingId) params.set('building_id', buildingId);
      const res = await axios.get(`${API}/sales/invoices?${params.toString()}`, axCfg);
      setPaymentInvoices((res.data || []).filter(i => !i.is_deleted));
    } catch { setPaymentInvoices([]); }
  };
  const onPaymentClientChange = async (v) => {
    // Phase 566 — Unified Client/Vendor/Building selector. The dropdown
    // value can be:
    //   • a regular cleaning_client_id              (clients & vendors)
    //   • a building's shadow client_id              (buildings with a
    //     pre-existing shadow client — buildingIdByValue.get(v) → bid)
    //   • "bldg:<building_id>"                       (orphan buildings)
    // In every case we resolve to (client_id, building_id) and pre-load
    // the allocation list so users see invoices for that entity.
    const bid = clientVendorBuildingOptions.buildingIdByValue.get(v);
    let next_client_id = v;
    let next_building_id = null;
    if (v && v.startsWith('bldg:')) {
      next_client_id = '';                   // resolved server-side
      next_building_id = v.slice(5);
    } else if (bid) {
      next_client_id = v;                    // shadow client_id
      next_building_id = bid;
    }
    setPaymentForm(f => ({
      ...f,
      _entity_value: v,
      client_id: next_client_id,
      building_id: next_building_id,
      allocations: [],
    }));
    await loadInvoicesForClient(next_client_id, next_building_id);
  };
  const setAllocation = (invoiceId, amount) => {
    setPaymentForm(f => {
      const arr = (f.allocations || []).filter(a => a.invoice_id !== invoiceId);
      const v = parseFloat(amount) || 0;
      if (v > 0) arr.push({ invoice_id: invoiceId, amount: v });
      return { ...f, allocations: arr };
    });
  };
  const savePayment = async () => {
    if (!paymentForm.client_id && !paymentForm.building_id) {
      toast.error(t('selectClientOrBuilding') || t('selectClient') || 'Select a customer / building'); return;
    }
    const amt = parseFloat(paymentForm.amount);
    if (!amt || amt <= 0) { toast.error('Amount must be greater than 0'); return; }
    const allocSum = (paymentForm.allocations || []).reduce((s, a) => s + a.amount, 0);
    if (allocSum > amt + 0.01) { toast.error('Allocations exceed payment amount'); return; }
    try {
      if (editingIncomeId) {
        // Phase 550 — When editing a Finance Income (Internal Cash) row:
        //   1) PUT description / notes / date in-place
        //      (this stops the "duplicate row after rename" bug).
        //   2) POST /allocate ONLY if the user actually touched
        //      allocations — otherwise the backend rejects the empty
        //      payload with 400 and silently leaves the rename
        //      "unsaved" looking. By skipping the call when there is
        //      nothing to allocate, the same record is updated in place.
        const incomePatch = {};
        if ((paymentForm.reference || '').trim()) incomePatch.description = paymentForm.reference.trim();
        if (paymentForm.notes) incomePatch.notes = paymentForm.notes;
        if (paymentForm.date) incomePatch.date = paymentForm.date;
        if (Object.keys(incomePatch).length > 0) {
          await axios.put(`${API}/company/income/${editingIncomeId}`, incomePatch, axCfg);
        }
        if ((paymentForm.allocations || []).length > 0) {
          await axios.post(`${API}/company/income/${editingIncomeId}/allocate`, {
            mode: 'multi',
            allocations: paymentForm.allocations,
            auto_allocate: false,
          }, axCfg);
        }
      } else {
        const body = {
          client_id: paymentForm.client_id,
          // Phase 566 — Pass building_id so the backend can persist it
          // on the payment doc (and lazy-resolve the shadow client if the
          // user picked a brand-new building).
          building_id: paymentForm.building_id || null,
          date: paymentForm.date, amount: amt,
          method: paymentForm.method, bank_account_id: paymentForm.bank_account_id || null,
          reference: paymentForm.reference, notes: paymentForm.notes,
          // Phase 526 — pass the payment classification through to the
          // backend so internal-cash payments get auto-routed to the
          // Internal Cash wallet.
          payment_account: paymentForm.payment_account || 'company_cash',
          allocations: paymentForm.allocations,
        };
        if (editingPaymentId) {
          // Phase 540 — Edit existing payment (reverses old allocations
          // and re-applies new ones on the backend).
          await axios.put(`${API}/sales/payments/${editingPaymentId}`, body, axCfg);
        } else {
          await axios.post(`${API}/sales/payments`, body, axCfg);
        }
      }
      toast.success(t('saved'));
      setPaymentDialog(false);
      setEditingPaymentId(null);
      setEditingIncomeId(null);
      fetchPayments(); fetchInvoices(); fetchCustomers(); fetchFinanceIncome();
    } catch (e) {
      // Phase 553 — Safe-render structured 409 errors (duplicate
      // Payment Number etc.). Never pass an object to toast.error.
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : (detail?.message || e?.response?.data?.message || 'Error');
      toast.error(msg);
    }
  };
  const deletePayment = async (p) => {
    try {
      await axios.delete(`${API}/sales/payments/${p.sales_payment_id}`, axCfg);
      toast.success(t('deleted'));
      fetchPayments(); fetchInvoices(); fetchCustomers();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (detail?.message || 'Error'));
    }
  };
  // Phase 543 — Void a bank-linked sales payment. Reverses allocations
  // server-side and stamps an audit-log entry instead of hard-deleting.
  const voidPayment = async (p, reason) => {
    try {
      await axios.post(`${API}/sales/payments/${p.sales_payment_id}/void`, { reason: reason || '' }, axCfg);
      toast.success(t('paymentVoided') || 'Payment voided');
      fetchPayments(); fetchInvoices(); fetchCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };
  // Phase 543 — Is this payment locked from hard delete? A payment is
  // considered "bank-linked" when it carries a real (non Internal-Cash)
  // bank_account_id. Those touch official treasury and must be voided,
  // not deleted, to preserve accounting integrity.
  const isBankLinked = (p) => {
    const bid = p?.bank_account_id;
    if (!bid) return false;
    const acct = bankAccounts.find(a => a.account_id === bid);
    // The dedicated Internal Cash virtual wallet is NOT a real bank.
    if (!acct) return true; // unknown account → treat as bank-linked (safer)
    if (acct.is_internal_cash) return false;
    return true;
  };

  const filteredCustomers = (customers || []).filter(c =>
    !search || (c.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Phase 557 — UNIFIED Finance customer list.
  // Buildings are first-class entities alongside Clients/Vendors. The
  // table merges:
  //   • Regular customers from `/api/sales/customers` (kept as-is —
  //     totals already computed server-side).
  //   • One synthetic row per Building, with aggregates derived from the
  //     invoices/payments already loaded on this page so we don't fire
  //     extra round-trips and stay consistent with the Invoices filter.
  // Buildings carry a sentinel `client_id = "bldg:<building_id>"` so the
  // existing selection/detail logic works without a second state slot.
  const unifiedCustomerRows = React.useMemo(() => {
    const rows = (customers || []).map(c => ({ ...c, _kind: 'client' }));
    const allInvoices = invoices || [];
    const allPayments = payments || [];
    (buildings || []).forEach(b => {
      const invs = allInvoices.filter(i =>
        i.building_id === b.building_id &&
        !i.is_deleted &&
        (i.invoice_type || 'official_invoice') === 'official_invoice'
      );
      const pays = allPayments.filter(p =>
        p.building_id === b.building_id &&
        !p.is_deleted &&
        (p.payment_account || 'company_cash') !== 'internal_cash_tracking'
      );
      const total_invoiced = invs.reduce((s, i) => s + (Number(i.total) || 0), 0);
      const total_paid = pays.reduce((s, p) => s + (Number(p.amount) || 0), 0);
      rows.push({
        _kind: 'building',
        client_id: `bldg:${b.building_id}`,
        building_id: b.building_id,
        name: b.name || '—',
        email: '',
        phone: '',
        tin: b.tax_id || '',
        billing_address: b.location || '',
        total_invoiced: Math.round(total_invoiced * 100) / 100,
        invoice_count: invs.length,
        total_paid: Math.round(total_paid * 100) / 100,
        payment_count: pays.length,
        balance: Math.round((total_invoiced - total_paid) * 100) / 100,
      });
    });
    rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return rows;
  }, [customers, buildings, invoices, payments]);

  const filteredUnifiedCustomers = unifiedCustomerRows.filter(r =>
    !search || (r.name || '').toLowerCase().includes(search.toLowerCase())
  );

  // Phase 415 — When embedded inside Finance we render WITHOUT the outer
  // Layout chrome (Finance owns it). The inner content tree is otherwise
  // identical so the Sales feature surface stays exactly the same.
  const Container = embed ? React.Fragment : Layout;
  const containerProps = embed ? {} : {};
  return (
    <Container {...containerProps}>
      <div className={embed ? '' : 'px-6 py-6 max-w-7xl mx-auto'}>
        {!embed && (
          <>
            <h1 className="text-3xl font-bold mb-1" data-testid="sales-page-title">{t('sales') || 'Sales'}</h1>
            <p className="text-zinc-500 mb-6 text-sm">{t('salesSubtitle') || 'Customers, Invoices, Payments and Statements — connected to Cleaning Management & Finance.'}</p>
          </>
        )}

        {/* Tabs — hidden in embedded mode (Finance sidebar drives selection) */}
        {!embed && (
        <div className="flex gap-1 border-b border-zinc-200 mb-6" data-testid="sales-tabs">
          {TABS.map(k => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              data-testid={`sales-tab-${k}`}
              className={`px-4 py-2 text-sm border-b-2 transition-colors ${activeTab === k
                ? 'border-zinc-900 text-zinc-900 font-semibold'
                : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
            >
              {t(k) || k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
        )}

        {/* CUSTOMERS — Phase 436: nested detail view (Summary / Invoices /
            Payments / Statement) appears when a row is opened. */}
        {activeTab === 'customers' && !selectedCustomerId && (
          <div data-testid="sales-customers">
            <div className="flex items-center justify-between mb-4">
              <div className="relative w-72">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  className="pl-9 rounded-none"
                  placeholder={t('searchClients') || 'Search customers...'}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  data-testid="sales-customers-search"
                />
              </div>
            </div>
            <div className="bg-white border border-zinc-200">
              <table className="w-full text-sm">
                <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                  <th className="text-left px-4 py-2.5">{t('name') || 'Name'}</th>
                  <th className="text-left px-4 py-2.5">{t('email') || 'Email'}</th>
                  <th className="text-left px-4 py-2.5">{t('telephone') || t('phone') || 'Telephone'}</th>
                  <th className="text-right px-4 py-2.5">{t('totalInvoices') || t('invoices') || 'Total Invoices'}</th>
                  <th className="text-right px-4 py-2.5">{t('totalPayments') || t('payments') || 'Total Payments'}</th>
                  <th className="text-right px-4 py-2.5">{t('balance') || 'Balance'}</th>
                </tr></thead>
                <tbody>
                  {filteredUnifiedCustomers.map(c => (
                    <tr
                      key={c.client_id}
                      className="border-b border-zinc-100 hover:bg-zinc-50/50 cursor-pointer"
                      onClick={() => { setSelectedCustomerId(c.client_id); setCustomerSubTab('summary'); }}
                      data-testid={`sales-customer-row-${c.client_id}`}
                    >
                      <td className="px-4 py-2.5 font-medium text-zinc-900 hover:text-blue-700">
                        <div className="flex items-center gap-2">
                          <span>{c.name}</span>
                          {c._kind === 'building' && (
                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid={`sales-customer-badge-building-${c.client_id}`}>
                              {t('building') || 'Building'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-zinc-600 text-xs">{c.email || '-'}</td>
                      <td className="px-4 py-2.5 text-zinc-600 font-mono text-xs">{c.phone || '-'}</td>
                      <td className="px-4 py-2.5 text-right font-mono">{fmtMoney(c.total_invoiced || 0)}<span className="text-[10px] text-zinc-400 ml-1">({c.invoice_count})</span></td>
                      <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(c.total_paid || 0)}<span className="text-[10px] text-zinc-400 ml-1">({c.payment_count})</span></td>
                      <td className={`px-4 py-2.5 text-right font-mono font-semibold ${c.balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmtMoney(c.balance)}</td>
                    </tr>
                  ))}
                  {filteredUnifiedCustomers.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-12 text-zinc-400">{t('noData') || 'No customers'}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CUSTOMER DETAIL — Phase 436: inline detail with sub-tabs */}
        {activeTab === 'customers' && selectedCustomerId && (() => {
          // Phase 557 — Unified detail: the row can be a regular client OR
          // a building (selectedCustomerId = "bldg:<building_id>"). Both
          // share the same KPI/Invoices/Payments/Statement layout; only
          // the data source for invoices/payments differs.
          const cust = unifiedCustomerRows.find(c => c.client_id === selectedCustomerId);
          if (!cust) {
            return (
              <div className="p-12 text-center" data-testid="sales-customer-detail-missing">
                <p className="text-sm text-zinc-500">{t('customerNotFound') || 'Customer not found'}.</p>
                <button onClick={() => setSelectedCustomerId(null)} className="mt-3 text-xs text-blue-700 hover:underline">← {t('back') || 'Back'}</button>
              </div>
            );
          }
          const isBuilding = cust._kind === 'building';
          // Phase 1026d — Smart per-customer tab visibility.
          // Customers who DO use the Internal Invoicing / Internal Cash
          // workflow should see their full Invoices / Payments / Statement
          // tabs. Customers who do NOT (i.e. we only track their money as
          // bank transactions tagged with the client_id) should only see a
          // Payments tab listing the matched bank incomes — no synthetic
          // "Total Invoices", no Statement, no outstanding balance from
          // legacy data. Underlying DB rows are preserved.
          const allCustInvoices = isBuilding
            ? invoices.filter(i => i.building_id === cust.building_id && !i.is_deleted)
            : invoices.filter(i => i.client_id === selectedCustomerId);
          const allCustPayments = isBuilding
            ? payments.filter(p => p.building_id === cust.building_id && !p.is_deleted)
            : payments.filter(p => p.client_id === selectedCustomerId);
          // Internal-only subsets (what the user actually uses today).
          const internalInvoices = allCustInvoices.filter(i => i.invoice_type === 'internal_cash_tracking');
          const internalPayments = allCustPayments.filter(p => (p.payment_account || 'company_cash') === 'internal_cash_tracking');
          // Bank-side income rows tagged with this customer's client_id —
          // visible for everyone (reference only, never allocated).
          const bankMatchedIncome = (isBuilding
            ? []
            : (financeIncome || []).filter(fi => fi.client_id === selectedCustomerId && !fi.is_deleted));
          const hasInternal = internalInvoices.length > 0 || internalPayments.length > 0;
          // customerInvoices / customerPayments drive the detail subtabs.
          const customerInvoices = hasInternal ? internalInvoices : [];
          // For the Payments subtab: for internal users, merge internal
          // Finance-side payments + bank income rows tagged with the client.
          // For non-internal users, show only the bank matches.
          const bankAsPayments = bankMatchedIncome.map(fi => ({
            sales_payment_id: `bank:${fi.income_id}`,
            date: fi.date,
            amount: fi.amount,
            method: fi.source === 'cash' ? 'cash' : 'bank',
            bank_account_id: fi.bank_account_id,
            reference: fi.reference || fi.notes || '',
            allocations: [],
            _origin: 'bank',
          }));
          const customerPayments = hasInternal
            ? [...internalPayments, ...bankAsPayments]
            : bankAsPayments;
          const subTabs = hasInternal
            ? [
                { key: 'summary', label: t('summary') || 'Summary' },
                { key: 'invoices', label: `${t('invoices') || 'Invoices'} (${customerInvoices.length})` },
                { key: 'payments', label: `${t('payments') || 'Payments'} (${customerPayments.length})` },
                { key: 'statement', label: t('statement') || 'Statement' },
              ]
            : [
                { key: 'summary', label: t('summary') || 'Summary' },
                { key: 'payments', label: `${t('payments') || 'Payments'} (${customerPayments.length})` },
              ];
          return (
            <div data-testid="sales-customer-detail">
              {/* Header */}
              <div className="flex items-start justify-between mb-4 gap-3">
                <div>
                  <button
                    onClick={() => { setSelectedCustomerId(null); setCustomerSubTab('summary'); }}
                    className="text-xs text-zinc-500 hover:text-zinc-900 mb-1 flex items-center gap-1"
                    data-testid="sales-customer-detail-back"
                  >← {t('backToCustomers') || 'Back to Customers'}</button>
                  <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2" data-testid="sales-customer-detail-name">
                    {cust.name}
                    {isBuilding && (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="sales-customer-detail-building-badge">
                        {t('building') || 'Building'}
                      </span>
                    )}
                  </h2>
                  <div className="text-xs text-zinc-500 mt-1 flex items-center gap-3">
                    {cust.email && <span>{cust.email}</span>}
                    {cust.phone && <span className="font-mono">{cust.phone}</span>}
                    {cust.tin && <span className="font-mono">TIN: {cust.tin}</span>}
                    {isBuilding && cust.billing_address && <span>{cust.billing_address}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!isBuilding && (
                    <Button onClick={() => openCustomerEdit(cust)} variant="outline" className="rounded-none h-8 text-xs" data-testid="sales-customer-detail-edit"><PencilSimple size={12} className="mr-1" />{t('edit') || 'Edit'}</Button>
                  )}
                  <Button
                    onClick={() => {
                      openInvoiceCreate();
                      if (isBuilding) {
                        setInvoiceForm(f => ({ ...f, building_id: cust.building_id, invoiced_to: 'building', client_id: '' }));
                      } else {
                        setInvoiceForm(f => ({ ...f, client_id: selectedCustomerId, invoiced_to: 'client', building_id: '' }));
                      }
                    }}
                    className="rounded-none bg-zinc-950 h-8 text-xs"
                    data-testid="sales-customer-detail-new-invoice"
                  ><Plus size={12} className="mr-1" />{t('newInvoice') || 'New Invoice'}</Button>
                  {!isBuilding && (
                    <Button
                      onClick={() => { openPaymentCreate(); onPaymentClientChange(selectedCustomerId); }}
                      className="rounded-none bg-emerald-700 h-8 text-xs"
                      data-testid="sales-customer-detail-new-payment"
                    ><CurrencyDollar size={12} className="mr-1" />{t('recordPayment') || 'Record Payment'}</Button>
                  )}
                </div>
              </div>

              {/* KPI strip */}
              <div className="grid grid-cols-4 gap-3 mb-4" data-testid="sales-customer-detail-kpis">
                <div className="bg-white border border-zinc-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('totalInvoices') || 'Total Invoices'}</p>
                  <p className="text-lg font-mono font-bold mt-1">{fmtMoney(cust.total_invoiced || 0)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{cust.invoice_count} {cust.invoice_count === 1 ? t('invoice') : t('invoices')}</p>
                </div>
                <div className="bg-white border border-emerald-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700">{t('totalPayments') || 'Total Payments'}</p>
                  <p className="text-lg font-mono font-bold text-emerald-700 mt-1">{fmtMoney(cust.total_paid || 0)}</p>
                  <p className="text-[10px] text-zinc-400 mt-0.5">{cust.payment_count} {cust.payment_count === 1 ? t('payment') : t('payments')}</p>
                </div>
                <div className={`bg-white border p-3 ${cust.balance > 0 ? 'border-red-200' : 'border-emerald-200'}`}>
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('balance') || 'Balance'}</p>
                  <p className={`text-lg font-mono font-bold mt-1 ${cust.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(cust.balance)}</p>
                </div>
                <div className="bg-white border border-zinc-200 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-zinc-500">{t('contact') || 'Contact'}</p>
                  <p className="text-xs text-zinc-700 mt-1 truncate" title={cust.email || ''}>{cust.email || '—'}</p>
                  <p className="text-xs text-zinc-700 font-mono">{cust.phone || '—'}</p>
                </div>
              </div>

              {/* Inner tabs */}
              {/* Phase 1026d — guard: if the active sub-tab is not in the
                  current subTabs list (e.g. "statement" selected for a
                  bank-only customer), auto-fallback to summary. */}
              {(() => {
                const validKeys = new Set(subTabs.map(s => s.key));
                if (!validKeys.has(customerSubTab)) {
                  setTimeout(() => setCustomerSubTab('summary'), 0);
                }
                return null;
              })()}
              <div className="flex gap-1 border-b border-zinc-200 mb-4" data-testid="sales-customer-subtabs">
                {subTabs.map(st => (
                  <button
                    key={st.key}
                    onClick={() => setCustomerSubTab(st.key)}
                    data-testid={`sales-customer-subtab-${st.key}`}
                    className={`px-4 py-2 text-sm border-b-2 transition-colors ${customerSubTab === st.key
                      ? 'border-zinc-900 text-zinc-900 font-semibold'
                      : 'border-transparent text-zinc-500 hover:text-zinc-800'}`}
                  >{st.label}</button>
                ))}
              </div>

              {/* SUMMARY */}
              {customerSubTab === 'summary' && (
                <div className="space-y-4" data-testid="sales-customer-summary">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white border border-zinc-200 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('billingDetails') || 'Billing Details'}</p>
                      <div className="space-y-1 text-xs">
                        <div><span className="text-zinc-500">{t('name') || 'Name'}:</span> <span className="text-zinc-900">{cust.name}</span></div>
                        <div><span className="text-zinc-500">TIN:</span> <span className="font-mono text-zinc-900">{cust.tin || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('email') || 'Email'}:</span> <span className="text-zinc-900">{cust.email || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('telephone') || 'Telephone'}:</span> <span className="font-mono text-zinc-900">{cust.phone || '—'}</span></div>
                        <div><span className="text-zinc-500">{t('billingAddress') || 'Billing address'}:</span> <span className="text-zinc-900">{cust.billing_address || cust.address || '—'}</span></div>
                      </div>
                    </div>
                    <div className="bg-white border border-zinc-200 p-4">
                      <p className="text-[10px] uppercase tracking-wider text-zinc-400 mb-2">{t('recentActivity') || 'Recent Activity'}</p>
                      {customerInvoices.length === 0 && customerPayments.length === 0 ? (
                        <p className="text-xs text-zinc-400">{t('noActivity') || 'No activity yet.'}</p>
                      ) : (
                        <ul className="text-xs space-y-1.5">
                          {[
                            ...customerInvoices.map(i => ({ type: 'invoice', date: i.date, ref: i.invoice_no, amount: i.total })),
                            ...customerPayments.map(p => ({ type: 'payment', date: p.date, ref: p.reference || p.sales_payment_id?.slice(0, 8), amount: p.amount })),
                          ]
                            .sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.ref || '').localeCompare(a.ref || ''))
                            .slice(0, 8)
                            .map((row, i) => (
                              <li key={i} className="flex items-center justify-between border-b border-zinc-100 pb-1">
                                <span className="text-zinc-500 font-mono">{fmtDate(row.date)}</span>
                                <span className="text-zinc-700 truncate flex-1 mx-2">{row.type === 'invoice' ? `📄 ${row.ref}` : `💳 ${row.ref}`}</span>
                                <span className={`font-mono font-semibold ${row.type === 'invoice' ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(row.amount)}</span>
                              </li>
                            ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* INVOICES (filtered for this customer) */}
              {customerSubTab === 'invoices' && (
                <div className="bg-white border border-zinc-200" data-testid="sales-customer-invoices">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">№</th>
                      <th className="text-left px-4 py-2.5">{t('referenceNumber') || 'Ref #'}</th>
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-left px-4 py-2.5">{t('description')}</th>
                      <th className="text-right px-4 py-2.5">{t('total')}</th>
                      <th className="text-right px-4 py-2.5">{t('paid')}</th>
                      <th className="text-left px-4 py-2.5">{t('status')}</th>
                      <th className="w-32 px-4 py-2.5 text-right">{t('actions') || 'Actions'}</th>
                    </tr></thead>
                    <tbody>
                      {customerInvoices.map(inv => (
                        <tr key={inv.invoice_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`sales-customer-inv-row-${inv.invoice_id}`}>
                          <td className="px-4 py-2.5 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                          <td className="px-4 py-2.5 font-mono text-xs text-zinc-500" data-testid={`sales-customer-inv-ref-${inv.invoice_id}`}>{inv.reference_no || '—'}</td>
                          <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(inv.date)}</td>
                          <td className="px-4 py-2.5 text-zinc-600 truncate max-w-xs">
                            <div className="flex items-center gap-2 min-w-0">
                              {/* Phase 518 — INTERNAL CASH badge.
                                  Only shown for non-official invoices so
                                  the default UI stays clean. */}
                              {inv.invoice_type === 'internal_cash_tracking' && (
                                <span className="shrink-0 text-[9px] px-1.5 py-0.5 font-bold uppercase tracking-wider bg-amber-500 text-white border border-amber-600" data-testid={`sales-customer-inv-badge-internal-${inv.invoice_id}`} title={t('internalCashHint') || 'Internal Cash — excluded from Finance/VAT'}>
                                  {t('badgeInternalCash') || 'Internal'}
                                </span>
                              )}
                              <span className="truncate">{inv.description}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmtMoney(inv.total)}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(inv.paid_amount)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-wider border ${
                              inv.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                              inv.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                              'border-zinc-200 bg-zinc-50 text-zinc-600'
                            }`}>{inv.status === 'open' ? 'unpaid' : inv.status}</span>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="flex justify-end gap-0.5">
                              <button onClick={() => viewInvoicePdf(inv)} className="p-1.5 text-zinc-500 hover:text-blue-700 hover:bg-blue-50 rounded-sm" title={t('viewPdf') || 'View PDF'} data-testid={`sales-customer-inv-pdf-${inv.invoice_id}`}><FilePdf size={14} /></button>
                              <button onClick={() => openInvoiceEdit(inv)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm" title={t('edit') || 'Edit'} data-testid={`sales-customer-inv-edit-${inv.invoice_id}`}><PencilSimple size={14} /></button>
                              <button onClick={() => openInvoiceEmail(inv)} className="p-1.5 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm" title={t('sendByEmail') || 'Send by Email'} data-testid={`sales-customer-inv-email-${inv.invoice_id}`}><Envelope size={14} /></button>
                              <button onClick={() => setInvoiceDeleteTarget(inv)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm" title={t('delete') || 'Delete'} data-testid={`sales-customer-inv-delete-${inv.invoice_id}`}><Trash size={14} /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {customerInvoices.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-zinc-400 text-sm">{t('noInvoicesYet') || 'No invoices for this customer yet.'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}

              {/* PAYMENTS (filtered for this customer) */}
              {customerSubTab === 'payments' && (
                <div className="bg-white border border-zinc-200" data-testid="sales-customer-payments">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-right px-4 py-2.5">{t('amount')}</th>
                      <th className="text-left px-4 py-2.5">{t('method')}</th>
                      <th className="text-left px-4 py-2.5">{t('bankAccount')}</th>
                      <th className="text-left px-4 py-2.5">{t('reference')}</th>
                      {/* Phase 1026d — Allocated column shown ONLY for
                          internal-flow customers. Reference-only bank rows
                          never allocate. */}
                      {hasInternal && <th className="text-left px-4 py-2.5">{t('allocated') || 'Allocated'}</th>}
                      <th className="w-12 px-4 py-2.5"></th>
                    </tr></thead>
                    <tbody>
                      {customerPayments.map(p => {
                        const isBankRef = p._origin === 'bank';
                        const allocSum = (p.allocations || []).reduce((s, a) => s + (a.amount || 0), 0);
                        const bank = bankAccounts.find(b => b.account_id === p.bank_account_id);
                        return (
                          <tr key={p.sales_payment_id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`sales-customer-pay-row-${p.sales_payment_id}`}>
                            <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(p.date)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold text-emerald-700">{fmtMoney(p.amount)}</td>
                            <td className="px-4 py-2.5 text-zinc-600 capitalize">
                              {p.method}
                              {isBankRef && (
                                <span className="ml-2 inline-flex items-center text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 bg-sky-50 text-sky-700 border border-sky-200" title={t('fromBankAccounts') || 'From Bank Accounts (reference only)'}>
                                  {t('bank') || 'Bank'}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-zinc-600 text-xs">{bank ? bank.account_name || bank.bank_name : '-'}</td>
                            <td className="px-4 py-2.5 font-mono text-xs">{p.reference || '-'}</td>
                            {hasInternal && (
                              <td className="px-4 py-2.5 text-xs">
                                {isBankRef
                                  ? <span className="text-zinc-400">—</span>
                                  : <span className={allocSum >= p.amount - 0.01 ? 'text-emerald-700' : 'text-amber-700'}>{fmtMoney(allocSum)} / {fmtMoney(p.amount)}</span>}
                              </td>
                            )}
                            <td className="px-4 py-2.5">
                              {!isBankRef && (
                                <button onClick={() => deletePayment(p)} className="text-zinc-400 hover:text-red-600" data-testid={`sales-customer-pay-delete-${p.sales_payment_id}`}><Trash size={14} /></button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {customerPayments.length === 0 && <tr><td colSpan={hasInternal ? 7 : 6} className="text-center py-10 text-zinc-400 text-sm">{t('noPaymentsYet') || 'No payments for this customer yet.'}</td></tr>}
                    </tbody>
                  </table>
                  {!hasInternal && (
                    <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50 text-[11px] text-zinc-500" data-testid="sales-customer-bankonly-hint">
                      {t('bankReferenceOnlyHint') || 'This customer is tracked only via Bank Accounts. The payments shown here are matched by client ID and are reference-only — no invoices or statement are generated.'}
                      <span className="ml-2 font-semibold">
                        {t('total') || 'Total'}: {fmtMoney(customerPayments.reduce((s, p) => s + (p.amount || 0), 0))}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* STATEMENT (this customer) — Phase 558 redesign:
                  Date | Type | Details | Amount | Payment | Balance
                  with running balance and resolved invoice allocations. */}
              {customerSubTab === 'statement' && (
                <div data-testid="sales-customer-statement">
                  {statement ? (() => {
                    // Lookup tables — invoices loaded on this page already.
                    const invNoById = new Map((invoices || []).map(i => [i.invoice_id, i.invoice_no]));
                    const paymentById = new Map((payments || []).map(p => [p.sales_payment_id, p]));
                    const incomeById = new Map((financeIncome || []).map(fi => [fi.income_id, fi]));
                    const allocSummary = (entry) => {
                      // Returns an array of "INV-XXX (€amount)" strings for a payment entry.
                      let allocs = [];
                      if (entry.sales_payment_id && paymentById.has(entry.sales_payment_id)) {
                        allocs = paymentById.get(entry.sales_payment_id).allocations || [];
                      } else if (entry.income_id && incomeById.has(entry.income_id)) {
                        const fi = incomeById.get(entry.income_id);
                        allocs = fi.invoice_allocations || fi.allocations || [];
                      }
                      if (allocs.length === 0 && entry.invoice_no) {
                        // Backend best-effort match (single open invoice etc.)
                        return [`${entry.invoice_no} (${fmtMoney(entry.amount)})`];
                      }
                      return allocs.map(a => {
                        const no = invNoById.get(a.invoice_id) || a.invoice_id?.slice(0, 10) || '—';
                        return `${no} (${fmtMoney(a.amount)})`;
                      });
                    };
                    // Sort entries chronologically; invoices before payments on the same date.
                    const ordered = [...(statement.entries || [])].sort((a, b) => {
                      const d = (a.date || '').localeCompare(b.date || '');
                      if (d !== 0) return d;
                      return a.type === 'invoice' ? -1 : 1;
                    });
                    let running = 0;
                    const rows = ordered.map((e, idx) => {
                      const amt = Number(e.amount) || 0;
                      if (e.type === 'invoice') running += amt;
                      else running -= amt;
                      // Phase 558 — Payment label resolution. Prefer the
                      // sales_payments.payment_no (Phase 553) so users see
                      // the canonical "PAY-####" they recognise from the
                      // Payments tab; fall back to the user-entered
                      // reference, then to the short id.
                      let detailRef = '';
                      let typeLabel = e.type === 'invoice' ? (t('invoice') || 'Invoice') : (t('payment') || 'Payment');
                      let allocChips = [];
                      if (e.type === 'invoice') {
                        detailRef = e.invoice_no || e.reference || '';
                      } else {
                        const p = e.sales_payment_id ? paymentById.get(e.sales_payment_id) : null;
                        const fi = e.income_id ? incomeById.get(e.income_id) : null;
                        detailRef = (p && (p.payment_no || p.reference))
                          || (fi && (fi.description || ''))
                          || e.reference
                          || '';
                        allocChips = allocSummary(e);
                      }
                      return { e, amt, running: Math.round(running * 100) / 100, idx, detailRef, typeLabel, allocChips };
                    });
                    return (
                      <>
                        <div className="grid grid-cols-3 gap-3 mb-4">
                          <div className="bg-white border border-zinc-200 p-4"><p className="text-xs text-zinc-500">{t('totalInvoiced') || 'Invoiced'}</p><p className="text-lg font-mono font-bold mt-1">{fmtMoney(statement.total_invoiced)}</p></div>
                          <div className="bg-white border border-emerald-200 p-4"><p className="text-xs text-emerald-700">{t('totalPayments') || 'Payments'}</p><p className="text-lg font-mono font-bold text-emerald-700 mt-1">{fmtMoney(statement.total_payments)}</p></div>
                          <div className={`bg-white border p-4 ${statement.balance > 0 ? 'border-red-200' : 'border-emerald-200'}`}><p className="text-xs text-zinc-500">{t('balance') || 'Balance'}</p><p className={`text-lg font-mono font-bold mt-1 ${statement.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(statement.balance)}</p></div>
                        </div>
                        <div className="bg-white border border-zinc-200 overflow-hidden">
                          <table className="w-full text-sm" data-testid="sales-customer-statement-table">
                            <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                              <th className="text-left px-4 py-2.5 w-28">{t('date') || 'Date'}</th>
                              <th className="text-left px-4 py-2.5 w-28">{t('transactionType') || 'Type'}</th>
                              <th className="text-left px-4 py-2.5">{t('details') || 'Details'}</th>
                              <th className="text-right px-4 py-2.5 w-28">{t('amount') || 'Amount'}</th>
                              <th className="text-right px-4 py-2.5 w-28">{t('payment') || 'Payment'}</th>
                              <th className="text-right px-4 py-2.5 w-32">{t('balance') || 'Balance'}</th>
                            </tr></thead>
                            <tbody>
                              {rows.map(({ e, amt, running, idx, detailRef, typeLabel, allocChips }) => {
                                const isInv = e.type === 'invoice';
                                return (
                                  <tr key={idx} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`sales-customer-statement-row-${idx}`}>
                                    <td className="px-4 py-2.5 font-mono text-xs text-zinc-600 align-top">{fmtDate(e.date)}</td>
                                    <td className="px-4 py-2.5 align-top">
                                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${
                                        isInv ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                      }`} data-testid={`sales-customer-statement-type-${idx}`}>{typeLabel}</span>
                                    </td>
                                    <td className="px-4 py-2.5 align-top">
                                      <div className="flex flex-col gap-0.5">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {detailRef && (
                                            <span className="font-mono text-xs font-semibold text-zinc-900" data-testid={`sales-customer-statement-ref-${idx}`}>{detailRef}</span>
                                          )}
                                          {!isInv && e.source === 'finance' && (
                                            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-blue-50 text-blue-700 border border-blue-200" title={t('fromFinance') || 'From Finance'}>{t('fromFinance') || 'From Finance'}</span>
                                          )}
                                        </div>
                                        {e.description && <span className="text-xs text-zinc-500 truncate max-w-md">{e.description}</span>}
                                        {!isInv && allocChips.length > 0 && (
                                          <div className="flex items-center gap-1 flex-wrap mt-0.5" data-testid={`sales-customer-statement-allocs-${idx}`}>
                                            <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{t('allocatedTo') || 'Allocated to'}:</span>
                                            {allocChips.map((c, i) => (
                                              <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 text-zinc-700 border border-zinc-200">{c}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-2.5 text-right font-mono text-red-700 align-top" data-testid={`sales-customer-statement-amount-${idx}`}>{isInv ? fmtMoney(amt) : ''}</td>
                                    <td className="px-4 py-2.5 text-right font-mono text-emerald-700 align-top" data-testid={`sales-customer-statement-payment-${idx}`}>{!isInv ? fmtMoney(amt) : ''}</td>
                                    <td className={`px-4 py-2.5 text-right font-mono font-semibold align-top ${running > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid={`sales-customer-statement-balance-${idx}`}>{fmtMoney(running)}</td>
                                  </tr>
                                );
                              })}
                              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-zinc-400">{t('noData') || 'No entries'}</td></tr>}
                            </tbody>
                            {rows.length > 0 && (
                              <tfoot>
                                <tr className="bg-zinc-50 border-t-2 border-zinc-300 text-xs font-semibold">
                                  <td colSpan={3} className="px-4 py-2.5 text-zinc-700 uppercase tracking-wider">{t('total') || 'Total'}</td>
                                  <td className="px-4 py-2.5 text-right font-mono text-red-700">{fmtMoney(statement.total_invoiced)}</td>
                                  <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(statement.total_payments)}</td>
                                  <td className={`px-4 py-2.5 text-right font-mono ${statement.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(statement.balance)}</td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                      </>
                    );
                  })() : (
                    <p className="text-zinc-400 text-sm">{t('loading') || 'Loading…'}</p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* INVOICES — Phase 422 master-detail + Phase 438 global filters */}
        {(activeTab === 'invoices' || activeTab === 'internal_invoices') && (() => {
          // Phase 524 — Internal Invoices live in their own dedicated tab
          // (Finance → Customers → Invoices vs Internal Invoices). The
          // segmented Type filter is replaced by the tab choice itself, so
          // the rest of this block can treat `forcedType` as the active
          // filter regardless of the legacy `invFilterType` state.
          const forcedType = activeTab === 'internal_invoices' ? 'internal_cash' : 'official';
          const isInternalTab = activeTab === 'internal_invoices';
          const selected = invoices.find(i => i.invoice_id === selectedInvoiceId) || null;
          const minA = parseFloat(invFilterAmountMin);
          const maxA = parseFloat(invFilterAmountMax);
          const filteredInvoices = invoices.filter(i => {
            if (invoiceSearch) {
              const q = invoiceSearch.toLowerCase();
              const hit = (i.invoice_no || '').toLowerCase().includes(q)
                || (i.client_name || '').toLowerCase().includes(q)
                || (i.description || '').toLowerCase().includes(q);
              if (!hit) return false;
            }
            if (!matchesClientVendorBuilding(i, invFilterCustomer)) return false;
            // Phase 554 — Treat legacy "open" as "unpaid" so the
            // filter dropdown (which only exposes unpaid/partial/paid)
            // still matches historical rows in case the GET hydrator
            // is bypassed somewhere.
            if (invFilterStatus !== '__all__') {
              const s = (i.status === 'open' ? 'unpaid' : i.status);
              if (s !== invFilterStatus) return false;
            }
            // Phase 524 — Tab IS the type filter. Records with no
            // `invoice_type` field default to official.
            {
              const ty = i.invoice_type || 'official_invoice';
              if (forcedType === 'official' && ty !== 'official_invoice') return false;
              if (forcedType === 'internal_cash' && ty !== 'internal_cash_tracking') return false;
            }
            if (invFilterDateFrom && (i.date || '') < invFilterDateFrom) return false;
            if (invFilterDateTo && (i.date || '') > invFilterDateTo) return false;
            const total = Number(i.total) || 0;
            if (!isNaN(minA) && total < minA) return false;
            if (!isNaN(maxA) && total > maxA) return false;
            return true;
          });
          // Phase 559 — Totals derived from the CURRENTLY filtered list
          // (search / status / Client-Vendor-Building / date / amount
          // filters all flow through `filteredInvoices`, so this respects
          // every active filter automatically). All values rounded to 2
          // decimals using ROUND_HALF_UP to stay in lock-step with the
          // Phase-555 backend Decimal helper — no more 14.99/15.00 drift.
          const r2 = (n) => Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
          const filteredTotals = filteredInvoices.reduce((acc, i) => {
            const total = Number(i.total) || 0;
            const paid = Number(i.paid_amount) || 0;
            const vat = Number(i.vat_amount ?? i.vat ?? 0) || 0;
            acc.vat += vat;
            acc.total += total;
            acc.paid += paid;
            return acc;
          }, { vat: 0, total: 0, paid: 0 });
          filteredTotals.vat = r2(filteredTotals.vat);
          filteredTotals.total = r2(filteredTotals.total);
          filteredTotals.paid = r2(filteredTotals.paid);
          filteredTotals.unpaid = r2(filteredTotals.total - filteredTotals.paid);
          // Total count of invoices belonging to THIS tab (ignoring search
          // and other secondary filters) so the "x/y" badge stays meaningful.
          const tabTotalCount = invoices.filter(i => {
            const ty = i.invoice_type || 'official_invoice';
            return forcedType === 'internal_cash' ? ty === 'internal_cash_tracking' : ty === 'official_invoice';
          }).length;
          const activeFilterCount = (invFilterCustomer !== '__all__' ? 1 : 0)
            + (invFilterStatus !== '__all__' ? 1 : 0)
            + (invFilterDateFrom ? 1 : 0)
            + (invFilterDateTo ? 1 : 0)
            + (invFilterAmountMin ? 1 : 0)
            + (invFilterAmountMax ? 1 : 0);
          // Phase 1026e — Build a per-tab filter options list that only
          // includes clients/buildings that actually have invoices of
          // that invoice_type. For Internal Invoices this means the
          // dropdown surfaces only entities that use the internal
          // workflow — no stale clients from the master catalog.
          // Automatically updates when invoices are added, edited or
          // deleted (reactive via `invoices`).
          const tabRelevantInvoices = invoices.filter(i => {
            if (i.is_deleted) return false;
            const ty = i.invoice_type || 'official_invoice';
            return isInternalTab ? ty === 'internal_cash_tracking' : ty === 'official_invoice';
          });
          const clientIdsWithTab = new Set();
          const buildingIdsWithTab = new Set();
          tabRelevantInvoices.forEach(inv => {
            if (inv.client_id) clientIdsWithTab.add(inv.client_id);
            if (inv.building_id) buildingIdsWithTab.add(inv.building_id);
          });
          const localeCollator = new Intl.Collator(['el', 'en'], { sensitivity: 'base', ignorePunctuation: true });
          const scopedClientOpts = (clientVendorBuildingOptions.clients || [])
            .filter(c => clientIdsWithTab.has(c.client_id))
            .slice().sort((a, b) => localeCollator.compare(a.name || '', b.name || ''));
          const scopedBuildingOpts = (clientVendorBuildingOptions.buildings || [])
            .filter(r => buildingIdsWithTab.has(r.building_id) ||
                         clientIdsWithTab.has(r.value) /* shadow-client */)
            .slice().sort((a, b) => localeCollator.compare(a.name || '', b.name || ''));
          const scopedOptionsFlat = [
            ...scopedClientOpts.map(c => ({ value: c.client_id, label: c.name || '', group: t('clients') || 'Clients' })),
            ...scopedBuildingOpts.map(r => ({ value: r.value, label: r.name || '', group: t('buildings') || 'Buildings' })),
          ];
          const selectedFilterLabel = invFilterCustomer === '__all__'
            ? (t('all') || 'All')
            : (scopedOptionsFlat.find(o => o.value === invFilterCustomer)?.label || (t('all') || 'All'));
          const clearAllFilters = () => {
            setInvoiceSearch('');
            setInvFilterCustomer('__all__'); setInvFilterStatus('__all__');
            setInvFilterDateFrom(''); setInvFilterDateTo('');
            setInvFilterAmountMin(''); setInvFilterAmountMax('');
          };
          return (
            <div data-testid={isInternalTab ? 'sales-internal-invoices' : 'sales-invoices'}>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">
                    {isInternalTab
                      ? (t('internalInvoicesLabel') || 'Internal Invoices')
                      : (t('invoicesLabel') || 'Invoices')}
                  </h2>
                  <span className="text-xs text-zinc-400">({filteredInvoices.length}/{tabTotalCount})</span>
                  {/* Phase 524 — Static badge replaces the old segmented
                      filter. The tab choice IS the filter now, so the
                      indicator is read-only. */}
                  <span
                    className={`ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 border font-semibold ${
                      isInternalTab
                        ? 'bg-amber-500 text-white border-amber-600'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                    }`}
                    data-testid={`sales-invoice-tab-badge-${isInternalTab ? 'internal' : 'official'}`}
                    title={isInternalTab
                      ? (t('internalInvoicesHint') || 'Internal bookkeeping only — excluded from official Finance/VAT and bank account totals.')
                      : (t('officialInvoicesHint') || 'Official customer invoices — included in Finance/VAT and bank account totals.')}
                  >
                    {isInternalTab
                      ? (t('invoiceTypeInternalInvoices') || 'Internal Invoices')
                      : (t('invoiceTypeOfficial') || 'Official')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="text"
                    placeholder={t('search') ? `${t('search')} № / ${t('client') || 'customer'} / ${t('description') || 'description'}…` : 'Search…'}
                    value={invoiceSearch}
                    onChange={e => setInvoiceSearch(e.target.value)}
                    className="rounded-none h-8 w-72 text-xs"
                    data-testid="sales-invoice-search"
                  />
                  {/* Phase 560 — Single global "Export PDF" for the WHOLE
                      filtered list. Builds the same filter query the table
                      uses, opens the report in the inline PDF preview
                      modal (no forced download, no popup-blocker). */}
                  <Button
                    onClick={() => {
                      // Phase 726 — Instead of exporting immediately, open the
                      // column-picker dialog and defer the actual URL build
                      // until the user confirms.
                      const buildAndOpen = (selectedCols) => {
                        const qs = new URLSearchParams();
                        qs.set('invoice_type', isInternalTab ? 'internal_cash_tracking' : 'official_invoice');
                        if (invFilterCustomer && invFilterCustomer !== '__all__') {
                          if (invFilterCustomer.startsWith('bldg:')) {
                            qs.set('building_id', invFilterCustomer.slice(5));
                          } else {
                            const bid = clientVendorBuildingOptions.buildingIdByValue.get(invFilterCustomer);
                            if (bid) qs.set('building_id', bid);
                            else qs.set('client_id', invFilterCustomer);
                          }
                        }
                        if (invFilterStatus && invFilterStatus !== '__all__') qs.set('status', invFilterStatus);
                        if (invFilterDateFrom) qs.set('date_from', invFilterDateFrom);
                        if (invFilterDateTo) qs.set('date_to', invFilterDateTo);
                        if (invFilterAmountMin) qs.set('amount_min', invFilterAmountMin);
                        if (invFilterAmountMax) qs.set('amount_max', invFilterAmountMax);
                        if (invoiceSearch) qs.set('search', invoiceSearch);
                        if (selectedCols && selectedCols.length > 0) qs.set('cols', selectedCols.join(','));
                        const url = buildPdfUrl(`/sales/invoices/export-pdf?${qs.toString()}`, { disposition: 'inline' });
                        setInvoicePdfUrl(url);
                        setInvoicePdfTitle(`${isInternalTab ? (t('internalInvoicesLabel') || 'Internal Invoices') : (t('invoicesLabel') || 'Invoices')} — ${t('report') || 'Report'} (${filteredInvoices.length})`);
                        setInvoicePdfOpen(true);
                      };
                      setPendingPdfBuild(() => buildAndOpen);
                      setPdfColsOpen(true);
                    }}
                    variant="outline"
                    className="rounded-none h-8 text-xs"
                    data-testid="sales-invoice-export-pdf"
                  ><FilePdf size={14} className="mr-1" />{t('viewPdf') || 'View PDF'}</Button>
                  <Button onClick={() => openInvoiceCreate(isInternalTab ? 'internal_cash_tracking' : 'official_invoice')} className="rounded-none bg-zinc-950 h-8" data-testid={isInternalTab ? 'sales-add-internal-invoice' : 'sales-add-invoice'}><Plus size={14} className="mr-1" /> {isInternalTab ? (t('newInternalInvoice') || 'New Internal Invoice') : (t('newInvoice') || 'New Invoice')}</Button>
                </div>
              </div>

              {/* Phase 438 — Filter strip (Customer / Status / Date range /
                  Amount range). Active filter count badge + Clear all. */}
              <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 mb-3 flex items-end gap-2 flex-wrap" data-testid="sales-invoice-filters">
                <div>
                  {/* Phase 556/1026e — Centralized Client / Vendor / Building
                      filter. For the Internal Invoices tab, the dropdown
                      only lists entities that already have at least one
                      Internal Invoice, sorted alphabetically with a built-in
                      search. */}
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('clientVendorBuilding') || 'Client / Vendor / Building'}</Label>
                  {isInternalTab ? (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          role="combobox"
                          className="rounded-none h-7 w-56 text-xs flex items-center justify-between gap-2 px-2 border border-zinc-200 bg-white hover:bg-zinc-50"
                          data-testid="sales-invoice-filter-customer"
                          title={selectedFilterLabel}
                        >
                          <span className="truncate text-left flex-1">{selectedFilterLabel}</span>
                          <CaretDown size={12} className="opacity-50 shrink-0" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-0 rounded-none" align="start">
                        <Command className="rounded-none">
                          <CommandInput
                            placeholder={t('searchClient') || 'Search client…'}
                            data-testid="sales-invoice-filter-customer-search"
                            className="h-9"
                          />
                          <CommandList className="max-h-72">
                            <CommandEmpty>{t('noMatches') || 'No matches'}</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all__ all"
                                onSelect={() => { setInvFilterCustomer('__all__'); }}
                                data-testid="sales-invoice-filter-customer-opt-all"
                              >
                                <Check size={12} className={`mr-2 ${invFilterCustomer === '__all__' ? 'opacity-100' : 'opacity-0'}`} />
                                {t('all') || 'All'}
                              </CommandItem>
                            </CommandGroup>
                            {scopedClientOpts.length > 0 && (
                              <CommandGroup heading={t('clients') || 'Clients'}>
                                {scopedClientOpts.map(c => (
                                  <CommandItem
                                    key={`cf:${c.client_id}`}
                                    value={`${c.name} ${c.client_id}`}
                                    onSelect={() => setInvFilterCustomer(c.client_id)}
                                    data-testid={`sales-invoice-filter-customer-opt-${c.client_id}`}
                                  >
                                    <Check size={12} className={`mr-2 ${invFilterCustomer === c.client_id ? 'opacity-100' : 'opacity-0'}`} />
                                    <span className="truncate">{c.name}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                            {scopedBuildingOpts.length > 0 && (
                              <CommandGroup heading={t('buildings') || 'Buildings'}>
                                {scopedBuildingOpts.map(r => (
                                  <CommandItem
                                    key={`bf:${r.value}`}
                                    value={`${r.name} ${r.value}`}
                                    onSelect={() => setInvFilterCustomer(r.value)}
                                    data-testid={`sales-invoice-filter-customer-opt-${r.value}`}
                                  >
                                    <Check size={12} className={`mr-2 ${invFilterCustomer === r.value ? 'opacity-100' : 'opacity-0'}`} />
                                    <span className="truncate">{r.name}</span>
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            )}
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Select value={invFilterCustomer} onValueChange={setInvFilterCustomer}>
                      <SelectTrigger className="rounded-none h-7 w-56 text-xs" data-testid="sales-invoice-filter-customer"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                        {clientVendorBuildingOptions.clients.length > 0 && (
                          <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('clients') || 'Clients'}</div>
                        )}
                        {clientVendorBuildingOptions.clients.map(c => <SelectItem key={`c:${c.client_id}`} value={c.client_id}>{c.name}</SelectItem>)}
                        {clientVendorBuildingOptions.buildings.length > 0 && (
                          <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('buildings') || 'Buildings'}</div>
                        )}
                        {clientVendorBuildingOptions.buildings.map(r => <SelectItem key={`b:${r.value}`} value={r.value}>{r.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('status') || 'Status'}</Label>
                  <Select value={invFilterStatus} onValueChange={setInvFilterStatus}>
                    <SelectTrigger className="rounded-none h-7 w-32 text-xs" data-testid="sales-invoice-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      <SelectItem value="unpaid">{t('unpaid') || 'Unpaid'}</SelectItem>
                      <SelectItem value="partial">{t('partial') || 'Partial'}</SelectItem>
                      <SelectItem value="paid">{t('paid') || 'Paid'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('from') || 'From'}</Label>
                  <Input type="date" value={invFilterDateFrom} onChange={e => setInvFilterDateFrom(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="sales-invoice-filter-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('to') || 'To'}</Label>
                  <Input type="date" value={invFilterDateTo} onChange={e => setInvFilterDateTo(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="sales-invoice-filter-to" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('minAmount') || 'Min €'}</Label>
                  <Input type="number" step="0.01" value={invFilterAmountMin} onChange={e => setInvFilterAmountMin(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="0" data-testid="sales-invoice-filter-min" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('maxAmount') || 'Max €'}</Label>
                  <Input type="number" step="0.01" value={invFilterAmountMax} onChange={e => setInvFilterAmountMax(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="∞" data-testid="sales-invoice-filter-max" />
                </div>
                {(activeFilterCount > 0 || invoiceSearch) && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="h-7 px-3 text-xs border border-zinc-300 bg-white hover:bg-zinc-100 ml-auto flex items-center gap-1"
                    data-testid="sales-invoice-filter-clear"
                  >✕ {t('clearAll') || 'Clear'} {activeFilterCount > 0 && <span className="text-zinc-400">({activeFilterCount})</span>}</button>
                )}
              </div>

              {/* Phase 535 — Customer Invoices use the same flat-table
                  layout as Finance → Suppliers → Invoices (full width when no
                  row is selected). Clicking a row sets `selectedInvoiceId`
                  and the Zoho-Books-style right-side preview slides open
                  while the table compresses to 60%. No row selected = no
                  preview pane = full-width table. */}
              <div className={selected ? 'flex gap-3 min-h-[60vh]' : ''}>
                <div className={selected ? 'w-[28%] min-w-[260px] self-start' : 'w-full'}>
                  {/* Phase 536 — When the preview panel is open, the left
                      list collapses to a simplified card list per item:
                      № · Customer · Date · Amount · Status. Full table
                      remains the default when no row is selected. */}
                  {selected ? (
                    <div className="bg-white border border-zinc-200" data-testid="sales-invoices-compact-list">
                      {filteredInvoices.map(inv => {
                        const isSel = selectedInvoiceId === inv.invoice_id;
                        const statusLabel = inv.status === 'paid' ? 'PAID' : inv.status === 'partial' ? 'PARTIAL' : 'UNPAID';
                        const statusClr = inv.status === 'paid' ? 'text-emerald-700' : inv.status === 'partial' ? 'text-amber-700' : 'text-zinc-500';
                        return (
                          <button
                            key={inv.invoice_id}
                            type="button"
                            onClick={() => setSelectedInvoiceId(inv.invoice_id)}
                            className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors ${isSel ? 'bg-emerald-50/60 border-l-4 border-l-emerald-600' : 'hover:bg-zinc-50/50 border-l-4 border-l-transparent'}`}
                            data-testid={`sales-inv-card-${inv.invoice_id}`}
                          >
                            <div className="font-mono text-xs font-semibold text-zinc-900">{inv.invoice_no}</div>
                            <div className="text-sm text-zinc-700 mt-0.5 truncate">{inv.customer_name || inv.client_name || inv.building_name || '—'}</div>
                            <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                              <span className="font-mono">{fmtDate(inv.date)}</span>
                              <span className="text-zinc-300">·</span>
                              <span className="font-mono font-semibold text-zinc-700">{fmtMoney(inv.total)}</span>
                              <span className="text-zinc-300">·</span>
                              <span className={`uppercase tracking-wider text-[10px] font-bold ${statusClr}`}>{statusLabel}</span>
                            </div>
                          </button>
                        );
                      })}
                      {filteredInvoices.length === 0 && <div className="px-4 py-12 text-center text-zinc-400 text-sm">{t('noData') || 'No invoices'}</div>}
                    </div>
                  ) : (
                  <div className="bg-white border border-zinc-200" data-testid={isInternalTab ? 'sales-internal-invoices-table' : 'sales-invoices-table'}>
                    <table className="w-full text-sm">
                      <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                        <th className="text-left px-4 py-2.5">№</th>
                        <th className="text-left px-4 py-2.5">{t('date') || 'Date'}</th>
                        <th className="text-left px-4 py-2.5">{t('client') || 'Customer'}</th>
                        <th className="text-left px-4 py-2.5">{t('description') || 'Description'}</th>
                        {/* Phase 549 — Reference # column placed right after
                            Description so users can match deposits or
                            supplier references at a glance. */}
                        <th className="text-left px-4 py-2.5">{t('referenceNo') || 'Reference #'}</th>
                        <th className="text-right px-4 py-2.5">{t('vat') || 'VAT'}</th>
                        <th className="text-right px-4 py-2.5">{t('total') || 'Total'}</th>
                        <th className="text-right px-4 py-2.5">{t('paid') || 'Paid'}</th>
                        <th className="text-left px-4 py-2.5">{t('status') || 'Status'}</th>
                        <th className="w-36 px-4 py-2.5 text-right">{t('actions') || 'Actions'}</th>
                      </tr></thead>
                      <tbody>
                        {filteredInvoices.map(inv => (
                          <tr
                            key={inv.invoice_id}
                            onClick={() => setSelectedInvoiceId(inv.invoice_id)}
                            className={`border-b border-zinc-100 cursor-pointer transition-colors ${selectedInvoiceId === inv.invoice_id ? 'bg-emerald-50/60' : 'hover:bg-zinc-50/50'}`}
                            data-testid={`sales-inv-row-${inv.invoice_id}`}
                          >
                            <td className="px-4 py-2.5 font-mono text-xs font-semibold">{inv.invoice_no}</td>
                            <td className="px-4 py-2.5 font-mono text-xs">{fmtDate(inv.date)}</td>
                            <td className="px-4 py-2.5">{inv.customer_name || inv.client_name || inv.building_name || '—'}</td>
                            <td className="px-4 py-2.5 text-zinc-600 max-w-[260px] truncate">{inv.description || '—'}</td>
                            <td className="px-4 py-2.5 font-mono text-xs text-zinc-600" data-testid={`sales-inv-ref-${inv.invoice_id}`}>{inv.reference_no || '—'}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs">{fmtMoney(inv.vat_amount ?? inv.vat ?? 0)}</td>
                            <td className="px-4 py-2.5 text-right font-mono font-semibold">{fmtMoney(inv.total)}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-emerald-700">{fmtMoney(inv.paid_amount || 0)}</td>
                            <td className="px-4 py-2.5">
                              <span className={`text-[10px] px-1.5 py-0.5 uppercase tracking-wider border ${
                                inv.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                                inv.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                                'border-zinc-200 bg-zinc-50 text-zinc-600'
                              }`}>{inv.status === 'open' ? 'unpaid' : inv.status}</span>
                            </td>
                            <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center justify-end gap-0.5" data-testid={`sales-inv-actions-${inv.invoice_id}`}>
                                <button onClick={() => viewInvoicePdf(inv)} className="p-1.5 text-zinc-500 hover:text-blue-700 hover:bg-blue-50 rounded-sm transition-colors" title={t('viewPdf') || 'View PDF'} data-testid={`sales-inv-pdf-${inv.invoice_id}`}><FilePdf size={15} /></button>
                                <button onClick={() => openInvoiceEdit(inv)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm transition-colors" title={t('edit') || 'Edit'} data-testid={`sales-inv-edit-${inv.invoice_id}`}><PencilSimple size={15} /></button>
                                <button onClick={() => openInvoiceEmail(inv)} className="p-1.5 text-zinc-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-sm transition-colors" title={t('sendByEmail') || 'Send by Email'} data-testid={`sales-inv-email-${inv.invoice_id}`}><Envelope size={15} /></button>
                                <button onClick={() => setInvoiceDeleteTarget(inv)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" title={t('delete') || 'Delete'} data-testid={`sales-inv-delete-${inv.invoice_id}`}><Trash size={15} /></button>
                              </div>
                            </td>
                          </tr>
                        ))}
                        {filteredInvoices.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-zinc-400">{t('noData') || 'No invoices'}</td></tr>}
                      </tbody>
                      {filteredInvoices.length > 0 && (
                        <tfoot data-testid="sales-invoices-totals-row">
                          {/* Phase 562 — Compact accounting-style footer.
                              Only 2 figures requested (Total VAT + Total
                              Amount), the rest were removed. */}
                          <tr className="bg-zinc-50 border-t-2 border-zinc-300 text-xs font-semibold">
                            <td colSpan={5} className="px-4 py-2.5 text-zinc-700 uppercase tracking-wider">
                              {filteredInvoices.length} {filteredInvoices.length === 1 ? (t('invoice') || 'invoice') : (t('invoices') || 'invoices')}
                            </td>
                            <td className="px-4 py-2.5 text-right text-zinc-500 uppercase tracking-wider text-[10px]">{t('totalVat') || 'Total VAT'}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-zinc-900" data-testid="sales-invoices-totals-vat">{fmtMoney(filteredTotals.vat)}</td>
                            <td className="px-4 py-2.5 text-right text-zinc-500 uppercase tracking-wider text-[10px]">{t('totalAmount') || 'Total Amount'}</td>
                            <td colSpan={2} className="px-4 py-2.5 text-right font-mono text-zinc-900 font-bold" data-testid="sales-invoices-totals-total">{fmtMoney(filteredTotals.total)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                  )}
                </div>

                {/* RIGHT — preview + action bar (only when a row is selected) */}
                {selected && (
                <section className="flex-1 min-w-0 bg-zinc-100 border border-zinc-200 self-start overflow-hidden" data-testid="sales-invoice-detail">
                  <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-zinc-200 bg-zinc-100">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-600 font-semibold flex items-center gap-2">
                      <FileText size={12} />
                      {selected.invoice_no}
                    </div>
                    <button onClick={() => setSelectedInvoiceId(null)} className="text-zinc-500 hover:text-zinc-900 p-1" title={t('close') || 'Close'} data-testid="sales-invoice-detail-close"><X size={14} /></button>
                  </div>
                  <>
                      {/* TOP ACTION BAR — Zoho style */}
                      <div className="flex items-center gap-1 px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex-wrap" data-testid="sales-invoice-action-bar">
                        <button onClick={() => openInvoiceEdit(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-edit"><PencilSimple size={12} />{t('edit') || 'Edit'}</button>
                        <button onClick={() => openInvoiceEmail(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-email"><Envelope size={12} />{t('sendEmail') || 'Send Email'}</button>
                        <button onClick={() => shareInvoiceLink(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-share"><Share size={12} />{t('share') || 'Share'}</button>
                        <button onClick={() => sendReminder(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-reminder"><Bell size={12} />{t('reminders') || 'Reminders'}</button>
                        <button onClick={() => printInvoicePdf(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-print"><Printer size={12} />{t('pdfPrint') || 'PDF / Print'}</button>
                        <button onClick={() => recordPaymentForInvoice(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-emerald-500 bg-emerald-600 text-white hover:bg-emerald-700" data-testid="sales-invoice-action-payment"><CurrencyDollar size={12} />{t('recordPayment') || 'Record Payment'}</button>
                        <button onClick={() => setPaymentsDialogInv(selected)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-800 bg-white text-zinc-900 hover:bg-zinc-100" data-testid="sales-invoice-action-view-payments"><Receipt size={12} />View Payments</button>
                        <div className="relative ml-auto">
                          <button onClick={() => setMoreMenuOpen(o => !o)} className="px-3 py-1.5 text-xs flex items-center gap-1.5 border border-zinc-300 bg-white hover:bg-zinc-50" data-testid="sales-invoice-action-more">{t('more') || 'More'}<DotsThree size={14} weight="bold" /></button>
                          {moreMenuOpen && (
                            <div className="absolute right-0 mt-1 w-48 bg-white border border-zinc-200 shadow-lg z-10" data-testid="sales-invoice-more-menu">
                              <button onClick={() => { cloneInvoice(selected); }} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex items-center gap-2" data-testid="sales-invoice-more-clone"><Copy size={12} />{t('clone') || 'Clone'}</button>
                              <button onClick={() => { markInvoiceAsSent(selected); }} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex items-center gap-2" data-testid="sales-invoice-more-mark-sent"><Check size={12} />{t('markAsSent') || 'Mark as Sent'}</button>
                              <button onClick={() => { downloadInvoicePdf(selected); setMoreMenuOpen(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-zinc-50 flex items-center gap-2" data-testid="sales-invoice-more-download"><DownloadSimple size={12} />{t('downloadPdf') || 'Download PDF'}</button>
                              <div className="border-t border-zinc-100" />
                              <button onClick={() => { setInvoiceDeleteTarget(selected); setMoreMenuOpen(false); }} className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid="sales-invoice-more-delete"><Trash size={12} />{t('delete') || 'Delete'}</button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* INVOICE PREVIEW — A4 paper style (Phase 537) */}
                      <div className="px-4 py-6 sm:px-8 overflow-y-auto" data-testid="sales-invoice-preview-shell">
                        <div className="bg-white shadow-lg mx-auto p-8 sm:p-12" style={{ width: '100%', maxWidth: '794px', minHeight: '1000px', aspectRatio: '210/297' }} data-testid="sales-invoice-preview">
                        {/* Phase 548 — Pro A4 header layout:
                            TOP-LEFT  → Company details (name, address, phone, email, VAT)
                            TOP-RIGHT → Invoice info (no, date, due, status)
                            BELOW     → Bill To + Amount Due, then items table.
                            This matches the way real accounting PDFs render
                            invoices (Xero / Zoho Books / QuickBooks). */}
                        <div className="flex items-start justify-between gap-8 mb-8 pb-4 border-b-2 border-zinc-800">
                          {/* Company block — top left */}
                          <div className="text-xs leading-snug min-w-0 flex-1" data-testid="inv-preview-company">
                            {companyProfile.company_logo && (
                              <img src={companyProfile.company_logo} alt="logo" className="h-12 mb-2 object-contain" />
                            )}
                            <div className="text-lg font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>
                              {companyProfile.company_name || ' '}
                            </div>
                            {companyProfile.company_name_line2 && (
                              <div className="text-[11px] text-zinc-600">{companyProfile.company_name_line2}</div>
                            )}
                            {(companyProfile.company_street_address || companyProfile.company_zip_code || companyProfile.company_city) && (
                              <div className="text-[11px] text-zinc-600 mt-1">
                                {companyProfile.company_street_address}
                                {companyProfile.company_street_address && (companyProfile.company_zip_code || companyProfile.company_city) && ', '}
                                {companyProfile.company_zip_code} {companyProfile.company_city}
                              </div>
                            )}
                            {companyProfile.company_country && (
                              <div className="text-[11px] text-zinc-600">{companyProfile.company_country}</div>
                            )}
                            <div className="flex flex-col gap-0.5 mt-1.5 text-[11px] text-zinc-600">
                              {companyProfile.company_phone && <div><span className="text-zinc-400">{t('phone') || 'Phone'}:</span> <span className="font-mono">{companyProfile.company_phone}</span></div>}
                              {companyProfile.company_email && <div><span className="text-zinc-400">{t('email') || 'Email'}:</span> <span className="font-mono">{companyProfile.company_email}</span></div>}
                              {companyProfile.company_vat_number && <div><span className="text-zinc-400">{t('companyVatRegNo')}:</span> <span className="font-mono">{companyProfile.company_vat_number}</span></div>}
                              {companyProfile.company_registration_number && <div><span className="text-zinc-400">{t('companyTicNo')}:</span> <span className="font-mono">{companyProfile.company_registration_number}</span></div>}
                            </div>
                          </div>
                          {/* Invoice block — top right */}
                          <div className="text-right flex-shrink-0">
                            <h2 className="text-3xl font-bold tracking-tight text-zinc-900" style={{ fontFamily: 'var(--font-heading)' }}>{t('invoice') || 'INVOICE'}</h2>
                            <div className="mt-2 text-xs space-y-0.5">
                              <div><span className="text-zinc-400">{t('invoiceNo') || 'Invoice No'}:</span> <span className="font-mono font-semibold text-zinc-900">{selected.invoice_no}</span></div>
                              <div><span className="text-zinc-400">{t('date') || 'Date'}:</span> <span className="font-mono">{fmtDate(selected.date)}</span></div>
                              {selected.due_date && <div><span className="text-zinc-400">{t('dueDate') || 'Due'}:</span> <span className="font-mono">{fmtDate(selected.due_date)}</span></div>}
                            </div>
                            <div className="mt-2">
                              <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${
                                selected.status === 'paid' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' :
                                selected.status === 'partial' ? 'border-amber-200 bg-amber-50 text-amber-700' :
                                'border-zinc-200 bg-zinc-50 text-zinc-600'
                              }`}>{selected.status === 'open' ? 'unpaid' : selected.status}</span>
                            </div>
                          </div>
                        </div>

                        {/* Bill To + Amount Due — moved below the header per Phase 548 */}
                        <div className="grid grid-cols-2 gap-6 mb-6 pb-4 border-b border-zinc-100">
                          <div>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('billTo') || 'Bill To'}</div>
                            {/* Phase 482 — Building invoices showed empty name
                                because UI only read client_name. Use the
                                unified customer_name resolved server-side. */}
                            <div className="text-sm font-semibold" data-testid="inv-preview-customer">
                              {selected.customer_name || selected.client_name || selected.building_name || '—'}
                            </div>
                            {selected.customer_type === 'building' && (
                              <div className="text-[10px] uppercase tracking-wider mt-0.5 inline-block px-1.5 py-0.5 border border-blue-200 bg-blue-50 text-blue-700">{t('building') || 'Building'}</div>
                            )}
                            {/* Only show building as secondary line when this is a
                                client-led invoice with a linked building. */}
                            {selected.customer_type !== 'building' && selected.building_name && (
                              <div className="text-xs text-zinc-500 mt-0.5">{selected.building_name}</div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('amountDue') || 'Amount Due'}</div>
                            <div className="text-2xl font-mono font-bold text-zinc-900">{fmtMoney((selected.total || 0) - (selected.paid_amount || 0))}</div>
                            <div className="text-[11px] text-zinc-500 mt-0.5">
                              {t('paid') || 'Paid'}: <span className="font-mono text-emerald-700">{fmtMoney(selected.paid_amount)}</span>
                              {' / '}
                              {t('total') || 'Total'}: <span className="font-mono">{fmtMoney(selected.total)}</span>
                            </div>
                          </div>
                        </div>

                        <table className="w-full text-sm" data-testid="sales-invoice-preview-items">
                          <thead>
                            <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500">
                              <th className="text-left py-2">{t('description') || 'Description'}</th>
                              <th className="text-right py-2 w-16">{t('quantity') || 'Qty'}</th>
                              <th className="text-right py-2 w-24">{t('rate') || 'Rate'}</th>
                              <th className="text-right py-2 w-20">{t('vat') || 'VAT'}</th>
                              <th className="text-right py-2 w-28">{t('amount') || 'Amount'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selected.items && selected.items.length > 0 ? selected.items : [{
                              description: selected.description || '—',
                              quantity: 1,
                              rate: selected.amount_net || selected.total,
                              vat_rate: selected.vat_rate || 0,
                              amount_total: selected.total,
                            }]).map((it, i) => (
                              <tr key={i} className="border-b border-zinc-50">
                                <td className="py-2">{it.description || '—'}</td>
                                <td className="text-right py-2 font-mono">{it.quantity || 1}</td>
                                <td className="text-right py-2 font-mono">{fmtMoney(it.rate)}</td>
                                <td className="text-right py-2 font-mono text-xs text-zinc-500">{it.vat_rate || 0}%</td>
                                <td className="text-right py-2 font-mono font-semibold">{fmtMoney(it.amount_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr><td colSpan={4} className="text-right py-1.5 text-xs text-zinc-500">{t('subtotal') || 'Subtotal'}</td><td className="text-right py-1.5 font-mono">{fmtMoney(selected.amount_net)}</td></tr>
                            <tr><td colSpan={4} className="text-right py-1.5 text-xs text-zinc-500">{t('vat') || 'VAT'}</td><td className="text-right py-1.5 font-mono">{fmtMoney(selected.vat_amount)}</td></tr>
                            <tr className="border-t border-zinc-300"><td colSpan={4} className="text-right py-2 text-sm font-semibold">{t('total') || 'Total'}</td><td className="text-right py-2 font-mono text-lg font-bold">{fmtMoney(selected.total)}</td></tr>
                          </tfoot>
                        </table>

                        {selected.notes && (
                          <div className="mt-6 pt-4 border-t border-zinc-100">
                            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-1">{t('notes') || 'Notes'}</div>
                            <p className="text-xs text-zinc-600 whitespace-pre-wrap">{selected.notes}</p>
                          </div>
                        )}
                      </div>
                      </div>
                  </>
                </section>
                )}
              </div>
            </div>
          );
        })()}

        {/* PAYMENTS — Phase 438 global filters. Phase 528: reverted to a
            single-tab renderer; Internal Cash now lives in its own
            customer-centric view (`activeTab === 'internal_cash'`). */}
        {activeTab === 'payments' && (() => {
          const minA = parseFloat(payFilterAmountMin);
          const maxA = parseFloat(payFilterAmountMax);
          const filteredPayments = payments.filter(p => {
            // Phase 528 — Internal Cash payments are surfaced under
            // Finance → Customers → Internal Cash. They never appear in
            // the regular Payments tab so the official view stays clean.
            // Phase 542 — Also exclude soft-deleted payments AND payments
            // sourced from `finance_income` mirrors that were created as
            // Internal Cash so the strict separation between Official
            // and Internal Cash flows holds end-to-end.
            if (p.is_deleted) return false;
            if ((p.payment_account || 'company_cash') === 'internal_cash_tracking') return false;
            if ((p.source || '') === 'finance_income' && p.finance_income_id) {
              const inc = (financeIncome || []).find(e => e.income_id === p.finance_income_id);
              if (inc && inc.affects_treasury === false) return false;
            }
            // Phase 556-fix — Use the shared matcher so building filters
            // resolve via building_id even when the selected dropdown
            // value is a shadow client_id (or vice versa).
            if (!matchesClientVendorBuilding(p, payFilterCustomer)) return false;
            if (payFilterMethod !== '__all__' && p.method !== payFilterMethod) return false;
            // Phase 553 — Status filter (computed from allocations[]).
            if (payFilterStatus !== '__all__') {
              const allocSum = (p.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
              const amtP = Number(p.amount) || 0;
              let st = 'unpaid';
              if (p.is_voided) st = 'voided';
              else if (allocSum > 0) st = (allocSum + 0.005 < amtP) ? 'partial' : 'paid';
              if (st !== payFilterStatus) return false;
            }
            if (payFilterRef) {
              const q = payFilterRef.toLowerCase();
              const hit = (p.reference || '').toLowerCase().includes(q)
                || (p.notes || '').toLowerCase().includes(q)
                || (p.client_name || '').toLowerCase().includes(q);
              if (!hit) return false;
            }
            if (payFilterDateFrom && (p.date || '') < payFilterDateFrom) return false;
            if (payFilterDateTo && (p.date || '') > payFilterDateTo) return false;
            const amt = Number(p.amount) || 0;
            if (!isNaN(minA) && amt < minA) return false;
            if (!isNaN(maxA) && amt > maxA) return false;
            return true;
          });
          // Phase 542 — Excludes internal-cash AND soft-deleted from the "x/y" denominator too.
          const tabTotalPay = payments.filter(p => {
            if (p.is_deleted) return false;
            if ((p.payment_account || 'company_cash') === 'internal_cash_tracking') return false;
            if ((p.source || '') === 'finance_income' && p.finance_income_id) {
              const inc = (financeIncome || []).find(e => e.income_id === p.finance_income_id);
              if (inc && inc.affects_treasury === false) return false;
            }
            return true;
          }).length;
          const activePayFilters = (payFilterCustomer !== '__all__' ? 1 : 0)
            + (payFilterMethod !== '__all__' ? 1 : 0)
            + (payFilterStatus !== '__all__' ? 1 : 0)
            + (payFilterRef ? 1 : 0)
            + (payFilterDateFrom ? 1 : 0) + (payFilterDateTo ? 1 : 0)
            + (payFilterAmountMin ? 1 : 0) + (payFilterAmountMax ? 1 : 0);
          const clearPayFilters = () => {
            setPayFilterCustomer('__all__'); setPayFilterMethod('__all__');
            setPayFilterStatus('__all__');
            setPayFilterRef(''); setPayFilterDateFrom(''); setPayFilterDateTo('');
            setPayFilterAmountMin(''); setPayFilterAmountMax('');
          };
          return (
            <div data-testid="sales-payments">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">{t('paymentsLabel') || t('payments') || 'Payments'}</h2>
                  <span className="text-xs text-zinc-400">({filteredPayments.length}/{tabTotalPay})</span>
                  <span
                    className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 border font-semibold bg-emerald-50 text-emerald-700 border-emerald-300"
                    data-testid="sales-payment-tab-badge-official"
                    title={t('officialPaymentsHint') || 'Official customer payments — affect treasury/bank balances and official Finance/VAT reports.'}
                  >
                    {t('paymentTypeOfficial') || 'Official'}
                  </span>
                </div>
                <Button onClick={() => openPaymentCreate('', 'company_cash')} className="rounded-none bg-zinc-950 h-8" data-testid="sales-add-payment"><Plus size={14} className="mr-1" /> {t('newPayment') || 'New Payment'}</Button>
              </div>

              {/* Filter strip — Phase 556 uses the centralized
                  Client / Vendor / Building options helper, so the Invoices
                  filter, Payments filter and Internal Cash filter all stay
                  in sync after any future data-source refactor. */}
              <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 mb-3 flex items-end gap-2 flex-wrap" data-testid="sales-payment-filters">
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('clientVendorBuilding') || 'Client / Vendor / Building'}</Label>
                  <Select value={payFilterCustomer} onValueChange={setPayFilterCustomer}>
                    <SelectTrigger className="rounded-none h-7 w-56 text-xs" data-testid="sales-payment-filter-customer"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-80">
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      {clientVendorBuildingOptions.clients.length > 0 && (
                        <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('clients') || 'Clients'}</div>
                      )}
                      {clientVendorBuildingOptions.clients.map(c => <SelectItem key={`c:${c.client_id}`} value={c.client_id}>{c.name}</SelectItem>)}
                      {clientVendorBuildingOptions.buildings.length > 0 && (
                        <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('buildings') || 'Buildings'}</div>
                      )}
                      {clientVendorBuildingOptions.buildings.map(r => <SelectItem key={`b:${r.value}`} value={r.value}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('method') || 'Method'}</Label>
                  <Select value={payFilterMethod} onValueChange={setPayFilterMethod}>
                    <SelectTrigger className="rounded-none h-7 w-28 text-xs" data-testid="sales-payment-filter-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      <SelectItem value="cash">{t('cash') || 'Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankTransfer') || 'Bank'}</SelectItem>
                      <SelectItem value="card">{t('card') || 'Card'}</SelectItem>
                      <SelectItem value="cheque">{t('cheque') || 'Cheque'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Phase 553 — Status filter (All / Paid / Partial / Unpaid). */}
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('status') || 'Status'}</Label>
                  <Select value={payFilterStatus} onValueChange={setPayFilterStatus}>
                    <SelectTrigger className="rounded-none h-7 w-32 text-xs" data-testid="sales-payment-filter-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                      <SelectItem value="paid">{t('badgePaid') || 'PAID'}</SelectItem>
                      <SelectItem value="partial">{t('badgePartial') || 'PARTIAL'}</SelectItem>
                      <SelectItem value="unpaid">{t('badgeUnpaid') || 'UNPAID'}</SelectItem>
                      <SelectItem value="voided">{t('badgeVoided') || 'VOIDED'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  {/* Phase 553 — Field renamed "Reference" → "Payment Number". */}
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('paymentNumber') || 'Payment Number'}</Label>
                  <Input type="text" value={payFilterRef} onChange={e => setPayFilterRef(e.target.value)} className="rounded-none h-7 w-44 text-xs font-mono" placeholder={t('search') ? `${t('search')}…` : 'Search…'} data-testid="sales-payment-filter-ref" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('from') || 'From'}</Label>
                  <Input type="date" value={payFilterDateFrom} onChange={e => setPayFilterDateFrom(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="sales-payment-filter-from" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('to') || 'To'}</Label>
                  <Input type="date" value={payFilterDateTo} onChange={e => setPayFilterDateTo(e.target.value)} className="rounded-none h-7 w-44 min-w-[160px] shrink-0 text-xs font-mono" data-testid="sales-payment-filter-to" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('minAmount') || 'Min €'}</Label>
                  <Input type="number" step="0.01" value={payFilterAmountMin} onChange={e => setPayFilterAmountMin(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="0" data-testid="sales-payment-filter-min" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500 block mb-0.5">{t('maxAmount') || 'Max €'}</Label>
                  <Input type="number" step="0.01" value={payFilterAmountMax} onChange={e => setPayFilterAmountMax(e.target.value)} className="rounded-none h-7 w-24 text-xs font-mono" placeholder="∞" data-testid="sales-payment-filter-max" />
                </div>
                {activePayFilters > 0 && (
                  <button
                    type="button"
                    onClick={clearPayFilters}
                    className="h-7 px-3 text-xs border border-zinc-300 bg-white hover:bg-zinc-100 ml-auto flex items-center gap-1"
                    data-testid="sales-payment-filter-clear"
                  >✕ {t('clearAll') || 'Clear'} <span className="text-zinc-400">({activePayFilters})</span></button>
                )}
              </div>

              <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50">
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('date') || 'Date'}</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('paymentNumberOrRef') || t('paymentNumber') || 'Payment # / Ref'}</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('client') || 'Client'}</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('amount') || 'Amount'}</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('unusedAmount') || 'Unused'}</th>
                      <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('allocatedAmount') || 'Allocated'}</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('status') || 'Status'}</th>
                      <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('method') || 'Method'}</th>
                      <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('actions') || 'Actions'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map(p => {
                      // Phase 541 — Same accounting math as Internal Cash:
                      // allocated = Σ allocations.amount, unused = amount - allocated,
                      // status = PAID / PARTIAL / UNPAID.
                      // Phase 543 — Voided payments render greyed-out with
                      // a VOIDED badge instead of a status badge. Their
                      // allocations are already reversed server-side.
                      const allocSum = (p.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                      const unused = Math.max(0, Number(p.amount || 0) - allocSum);
                      let stCode = 'unpaid', stLabel = (t('badgeUnpaid') || 'UNPAID'), stCls = 'border-zinc-200 bg-zinc-50 text-zinc-600';
                      if (p.is_voided) {
                        stCode = 'voided';
                        stLabel = (t('badgeVoided') || 'VOIDED');
                        stCls = 'border-red-200 bg-red-50 text-red-700';
                      } else if (allocSum > 0) {
                        if (allocSum + 0.005 < Number(p.amount || 0)) {
                          stCode = 'partial'; stLabel = (t('badgePartial') || 'PARTIAL'); stCls = 'border-amber-200 bg-amber-50 text-amber-700';
                        } else {
                          stCode = 'paid'; stLabel = (t('badgePaid') || 'PAID'); stCls = 'border-emerald-200 bg-emerald-50 text-emerald-700';
                        }
                      }
                      const bankLinked = isBankLinked(p);
                      const isVoided = !!p.is_voided;
                      // Phase 619 — Finance-origin bank receipts (no
                      // sales_payment mirror). Allocation IS allowed; only
                      // the bank transaction itself is protected (no
                      // delete, no amount/bank/source edit). The row stays
                      // clickable; the dialog opens in `editingIncomeId`
                      // mode and saves via /company/income/{id}/allocate.
                      const isReadOnlyFinance = (p.source === 'finance') && !!p.read_only;
                      return (
                        <tr
                          key={p.sales_payment_id}
                          onClick={() => {
                            if (isVoided) return;
                            if (isReadOnlyFinance) {
                              openEditFinancePayment(p);
                              return;
                            }
                            openEditPayment(p);
                          }}
                          className={`border-b border-zinc-100 ${isVoided ? 'opacity-60' : 'hover:bg-zinc-50/60 cursor-pointer'} ${isReadOnlyFinance && !isVoided ? 'bg-blue-50/20' : ''}`}
                          data-testid={`sales-pay-row-${p.sales_payment_id}`}
                        >
                          <td className="px-3 py-2 font-mono">{fmtDate(p.date)}</td>
                          <td className="px-3 py-2 font-mono text-zinc-600">
                            {p.reference || p.sales_payment_id}
                            {isReadOnlyFinance && (
                              <span className="ml-2 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-blue-200 bg-blue-50 text-blue-700 inline-flex items-center gap-0.5" data-testid={`sales-pay-from-finance-${p.sales_payment_id}`} title={t('readOnlyEditInFinance') || 'Read-only. Edit in Finance.'}>
                                <Lock size={9} weight="bold" /> {t('fromFinance') || 'From Finance'}
                              </span>
                            )}
                            {isReadOnlyFinance && p.bank_account_name && (
                              <span className="ml-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800 inline-flex items-center gap-0.5" title={p.bank_account_id || ''}>
                                🏦 {p.bank_account_name}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-medium" data-testid={`sales-pay-customer-${p.sales_payment_id}`}>
                            {p.customer_name || p.client_name || p.building_name || '—'}
                            {p.customer_type === 'building' && <span className="ml-1 text-[9px] uppercase tracking-wider px-1 py-0.5 border border-blue-200 bg-blue-50 text-blue-700">B</span>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono font-semibold">{fmtMoney(p.amount)}</td>
                          <td className={`px-3 py-2 text-right font-mono ${unused > 0 && !isVoided ? 'text-amber-700 font-semibold' : 'text-zinc-400'}`} data-testid={`sales-pay-unused-${p.sales_payment_id}`}>{fmtMoney(isVoided ? 0 : unused)}</td>
                          <td className={`px-3 py-2 text-right font-mono ${allocSum > 0 && !isVoided ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`} data-testid={`sales-pay-allocated-${p.sales_payment_id}`}>{fmtMoney(isVoided ? 0 : allocSum)}</td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${stCls}`} data-testid={`sales-pay-status-${p.sales_payment_id}`}>{stLabel}</span>
                          </td>
                          <td className="px-3 py-2 capitalize text-zinc-600">{p.method || '—'}</td>
                          <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-center gap-0.5" data-testid={`sales-pay-actions-${p.sales_payment_id}`}>
                              {!isVoided && (
                                <button
                                  onClick={() => isReadOnlyFinance ? openEditFinancePayment(p) : openEditPayment(p)}
                                  className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm transition-colors"
                                  title={isReadOnlyFinance ? (t('allocate') || 'Allocate') : (t('edit') || 'Edit')}
                                  data-testid={`sales-pay-edit-${p.sales_payment_id}`}
                                ><PencilSimple size={14} /></button>
                              )}
                              {/* Phase 543 — Hide Delete when payment is
                                  linked to a real bank account; show
                                  Void instead so accounting integrity is
                                  preserved (no hard delete of treasury
                                  records).
                                  Phase 619 — Finance-origin rows: bank
                                  transaction is protected; never offer
                                  Delete or Void from the Sales tab
                                  (Finance owns deletion). */}
                              {!isVoided && !isReadOnlyFinance && bankLinked && (
                                <button
                                  onClick={() => { setPaymentVoidReason(''); setPaymentVoidTarget(p); }}
                                  className="p-1.5 text-zinc-500 hover:text-red-700 hover:bg-red-50 rounded-sm transition-colors"
                                  title={t('void') || 'Void'}
                                  data-testid={`sales-pay-void-${p.sales_payment_id}`}
                                ><Prohibit size={14} /></button>
                              )}
                              {!isVoided && !isReadOnlyFinance && !bankLinked && (
                                <button onClick={() => setPaymentDeleteTarget(p)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" title={t('delete') || 'Delete'} data-testid={`sales-pay-delete-${p.sales_payment_id}`}><Trash size={14} /></button>
                              )}
                              {!isVoided && isReadOnlyFinance && (
                                <span className="text-[9px] uppercase tracking-wider text-blue-600 px-1" title={t('bankTransactionProtected') || 'Bank transaction protected — manage in Finance'} data-testid={`sales-pay-protected-${p.sales_payment_id}`}>
                                  <Lock size={11} weight="bold" />
                                </span>
                              )}
                              {isVoided && (
                                <span className="text-[10px] uppercase tracking-wider text-zinc-400 px-2" title={p.void_reason || ''}>{t('voidedLockHint') || 'Locked'}</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredPayments.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-zinc-400">{t('noData') || 'No payments'}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* INTERNAL CASH — Phase 528. Customer-centric ledger for every
            internal-cash sales_payment. Replaces the previous Internal
            Payments tab AND the Internal Cash bank-account view: this is
            now the single place where Internal Cash activity is managed. */}
        {activeTab === 'internal_cash' && (() => {
          // Phase 550 — Internal Cash de-duplication: exclude
          // sales_payments that are auto-generated MIRRORS of a finance
          // income row (Phase 525's _allocate_income_to_invoices stamps
          // `source: 'finance_income'` + `finance_income_id` on them).
          // Without this filter the table shows the income row AND its
          // sales_payment twin → looks like a duplicate after editing.
          const internalSales = payments.filter(p =>
            (p.payment_account || '') === 'internal_cash_tracking'
            && !p.is_deleted
            && !((p.source || '') === 'finance_income' && p.finance_income_id)
          );
          // Phase 528-fix — Also merge Finance → Income entries flagged as
          // Internal Cash (`affects_treasury === false`). They're the
          // canonical source of all existing Internal Cash money in the
          // system (Cleaning Management surfaces them as "FROM FINANCE").
          const internalFinance = (financeIncome || []).filter(e => e.affects_treasury === false && !e.is_deleted);
          // Normalize into a single row shape so the table can iterate
          // both kinds uniformly.
          const clientNameOf = (cid) => (customers.find(c => c.client_id === cid)?.name)
            || (allClients.find(c => c.client_id === cid)?.name)
            || '—';
          const internalRows = [
            ...internalSales.map(p => ({
              key: `spay:${p.sales_payment_id}`,
              kind: 'sales_payment',
              row_id: p.sales_payment_id,
              client_id: p.client_id,
              client_name: p.client_name || clientNameOf(p.client_id),
              amount: Number(p.amount || 0),
              date: p.date,
              method: p.method || '',
              reference: p.reference || p.sales_payment_id,
              allocations: Array.isArray(p.allocations) ? p.allocations : [],
              source_label: 'Customer Payment',
            })),
            ...internalFinance.map(e => ({
              key: `finc:${e.income_id}`,
              kind: 'finance_income',
              row_id: e.income_id,
              client_id: e.client_id,
              client_name: clientNameOf(e.client_id),
              amount: Number(e.amount || 0),
              date: e.date,
              method: e.source || 'cash',
              // Phase 550 — Show the user-editable description as the
              // Reference when set, falling back to the immutable
              // income_id for legacy entries. This makes Reference edits
              // immediately visible in the Internal Cash table.
              reference: (e.description || '').trim() || e.income_id,
              // Phase 540 — Finance Income rows store allocations under
              // `invoice_allocations` (not `allocations`). Surface them so
              // the Internal Cash table shows correct Allocated / Unused /
              // Status badges for these entries too.
              allocations: Array.isArray(e.invoice_allocations) ? e.invoice_allocations : [],
              source_label: 'Finance',
            })),
          ];
          // Build invoice lookup so we can show invoice no + remaining.
          const invById = new Map();
          (invoices || []).forEach(i => invById.set(i.invoice_id, i));
          // Per-customer totals: internal invoiced + internal paid (covers BOTH sources).
          const totalsByClient = new Map();
          (invoices || []).filter(i => i.invoice_type === 'internal_cash_tracking' && !i.is_deleted).forEach(i => {
            const c = totalsByClient.get(i.client_id) || { invoiced: 0, paid: 0 };
            c.invoiced += Number(i.total || 0);
            totalsByClient.set(i.client_id, c);
          });
          internalRows.forEach(r => {
            if (!r.client_id) return;
            const c = totalsByClient.get(r.client_id) || { invoiced: 0, paid: 0 };
            c.paid += r.amount;
            totalsByClient.set(r.client_id, c);
          });
          // Apply secondary filters using the existing payment filter state.
          const minA = parseFloat(payFilterAmountMin);
          const maxA = parseFloat(payFilterAmountMax);
          const visible = internalRows.filter(r => {
            // Phase 556-fix — Shared matcher (see Invoices/Customer
            // Payments filters). Internal Cash rows can carry either a
            // client_id (regular customer) or a building_id (building
            // revenue) so resolving via the building map is essential.
            if (!matchesClientVendorBuilding(r, payFilterCustomer)) return false;
            if (payFilterMethod !== '__all__' && r.method !== payFilterMethod) return false;
            if (payFilterDateFrom && (r.date || '') < payFilterDateFrom) return false;
            if (payFilterDateTo && (r.date || '') > payFilterDateTo) return false;
            if (!isNaN(minA) && r.amount < minA) return false;
            if (!isNaN(maxA) && r.amount > maxA) return false;
            if (payFilterRef) {
              const q = payFilterRef.toLowerCase();
              const hit = (r.reference || '').toLowerCase().includes(q)
                || (r.client_name || '').toLowerCase().includes(q);
              if (!hit) return false;
            }
            return true;
          }).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
          const totalPaid = internalRows.reduce((s, r) => s + r.amount, 0);
          const totalInvoiced = Array.from(totalsByClient.values()).reduce((s, c) => s + c.invoiced, 0);
          const totalRemaining = totalInvoiced - totalPaid;
          const invoiceLabel = (r) => {
            if (!r.allocations.length) return '—';
            return r.allocations.map(a => {
              const inv = invById.get(a.invoice_id);
              return inv?.invoice_no || a.invoice_id;
            }).join(', ');
          };
          const remainingForClient = (cid) => {
            const c = totalsByClient.get(cid) || { invoiced: 0, paid: 0 };
            return c.invoiced - c.paid;
          };
          // Phase 539 — Per-row allocation math drives Allocated / Unused / Status.
          const allocatedOf = (r) => (r.allocations || []).reduce((s, a) => s + Number(a.amount || 0), 0);
          const statusOf = (r) => {
            const alloc = allocatedOf(r);
            if (alloc <= 0) return { code: 'unpaid', label: t('badgeUnpaid') || 'UNPAID', cls: 'border-zinc-200 bg-zinc-50 text-zinc-600' };
            if (alloc + 0.005 < r.amount) return { code: 'partial', label: t('badgePartial') || 'PARTIAL', cls: 'border-amber-200 bg-amber-50 text-amber-700' };
            return { code: 'paid', label: t('badgePaid') || 'PAID', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' };
          };
          const bankNameOf = (r) => {
            const src = r.kind === 'finance_income'
              ? financeIncome.find(e => e.income_id === r.row_id)
              : payments.find(p => p.sales_payment_id === r.row_id);
            const bid = src?.bank_account_id;
            if (!bid) return r.method === 'cash' ? (t('cash') || 'Cash') : '—';
            return bankAccounts.find(a => a.account_id === bid)?.name || '—';
          };
          return (
            <div data-testid="sales-internal-cash">
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-semibold">{t('internalCashLabel') || 'Internal Cash'}</h2>
                  <span className="text-xs text-zinc-400">({visible.length}/{internalRows.length})</span>
                  <span
                    className="ml-2 inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 border font-semibold bg-amber-500 text-white border-amber-600"
                    data-testid="sales-internal-cash-badge"
                    title={t('internalCashSectionHint') || 'Internal customer/invoice tracking — separate from official treasury and bank accounts.'}
                  >
                    {t('paymentTypeInternalCash') || 'Internal Cash'}
                  </span>
                </div>
                <Button
                  onClick={() => openPaymentCreate('', 'internal_cash_tracking')}
                  className="rounded-none bg-amber-600 hover:bg-amber-700 h-8"
                  data-testid="sales-add-internal-cash-payment"
                >
                  <Plus size={14} className="mr-1" /> {t('newInternalCashPayment') || 'New Internal Cash Payment'}
                </Button>
              </div>
              {/* Roll-up totals strip */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="border border-amber-200 bg-amber-50/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">{t('internalCashInvoicedLabel') || 'Internal Invoiced'}</div>
                  <div className="text-xl font-mono font-bold text-amber-900" data-testid="internal-cash-total-invoiced">&euro;{totalInvoiced.toFixed(2)}</div>
                </div>
                <div className="border border-emerald-200 bg-emerald-50/40 p-3">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{t('totalPaid') || 'Total Paid'}</div>
                  <div className="text-xl font-mono font-bold text-emerald-900" data-testid="internal-cash-total-paid">&euro;{totalPaid.toFixed(2)}</div>
                </div>
                <div className={`border p-3 ${totalRemaining > 0 ? 'border-red-200 bg-red-50/40' : 'border-zinc-200 bg-zinc-50/40'}`}>
                  <div className={`text-[10px] uppercase tracking-wider font-semibold ${totalRemaining > 0 ? 'text-red-700' : 'text-zinc-600'}`}>{t('totalRemaining') || 'Total Remaining'}</div>
                  <div className={`text-xl font-mono font-bold ${totalRemaining > 0 ? 'text-red-900' : 'text-zinc-900'}`} data-testid="internal-cash-total-remaining">&euro;{totalRemaining.toFixed(2)}</div>
                </div>
              </div>
              {/* Inline filters — minimal subset relevant for internal cash. */}
              <div className="flex items-center gap-2 mb-3 flex-wrap text-xs">
                <Select value={payFilterCustomer} onValueChange={setPayFilterCustomer}>
                  <SelectTrigger className="rounded-none h-8 w-56" data-testid="ic-filter-customer">
                    <SelectValue placeholder={t('clientVendorBuilding') || 'Client / Vendor / Building'} />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="__all__">{t('all') || 'All'}</SelectItem>
                    {clientVendorBuildingOptions.clients.length > 0 && (
                      <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('clients') || 'Clients'}</div>
                    )}
                    {clientVendorBuildingOptions.clients.map(c => <SelectItem key={`c:${c.client_id}`} value={c.client_id}>{c.name}</SelectItem>)}
                    {clientVendorBuildingOptions.buildings.length > 0 && (
                      <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-zinc-400 bg-zinc-50 border-t border-b border-zinc-100 mt-1">{t('buildings') || 'Buildings'}</div>
                    )}
                    {clientVendorBuildingOptions.buildings.map(r => <SelectItem key={`b:${r.value}`} value={r.value}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={payFilterDateFrom} onChange={e => setPayFilterDateFrom(e.target.value)} className="rounded-none h-8 w-44 min-w-[160px] shrink-0" placeholder={t('from') || 'From'} data-testid="ic-filter-from" />
                <Input type="date" value={payFilterDateTo} onChange={e => setPayFilterDateTo(e.target.value)} className="rounded-none h-8 w-44 min-w-[160px] shrink-0" placeholder={t('to') || 'To'} data-testid="ic-filter-to" />
                {(payFilterCustomer !== '__all__' || payFilterDateFrom || payFilterDateTo) && (
                  <Button variant="outline" className="rounded-none h-8 text-xs" onClick={() => { setPayFilterCustomer('__all__'); setPayFilterDateFrom(''); setPayFilterDateTo(''); }} data-testid="ic-clear-filters">
                    {t('clearFilters') || 'Clear'}
                  </Button>
                )}
              </div>
              {visible.length === 0 ? (
                <div className="border border-dashed border-amber-300 p-8 text-center text-sm text-amber-700">
                  {t('noInternalCashPayments') || 'No Internal Cash payments yet. New entries are auto-routed here when you tag them as Internal Cash on a customer payment.'}
                </div>
              ) : (
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-zinc-200 bg-zinc-50">
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('date') || 'Date'}</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('paymentNumberOrRef') || t('paymentNumber') || 'Payment # / Ref'}</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('client') || 'Client'}</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('amount') || 'Amount'}</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('unusedAmount') || 'Unused'}</th>
                        <th className="text-right px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('allocatedAmount') || 'Allocated'}</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('status') || 'Status'}</th>
                        <th className="text-left px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('method') || 'Method'}</th>
                        <th className="text-center px-3 py-2 font-semibold uppercase tracking-wider text-[10px]">{t('actions') || 'Actions'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visible.map(r => {
                        const allocated = allocatedOf(r);
                        const unused = Math.max(0, r.amount - allocated);
                        const st = statusOf(r);
                        // Phase 540 — Both sales_payment AND finance_income
                        // (Internal Cash) rows now open the allocation
                        // dialog so the user can allocate against open
                        // Internal Invoices directly from the row.
                        const rowClickable = !!r.client_id;
                        return (
                          <tr
                            key={r.key}
                            onClick={() => { if (rowClickable) editInternalCashRow(r); }}
                            className={`border-b border-zinc-100 hover:bg-amber-50/40 ${rowClickable ? 'cursor-pointer' : ''}`}
                            data-testid={`ic-row-${r.row_id}`}
                          >
                            <td className="px-3 py-2 font-mono">{r.date}</td>
                            <td className="px-3 py-2 font-mono text-zinc-600">{r.reference}</td>
                            <td className="px-3 py-2 font-medium">{r.client_name || '—'}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold">&euro;{r.amount.toFixed(2)}</td>
                            <td className={`px-3 py-2 text-right font-mono ${unused > 0 ? 'text-amber-700 font-semibold' : 'text-zinc-400'}`}>&euro;{unused.toFixed(2)}</td>
                            <td className={`px-3 py-2 text-right font-mono ${allocated > 0 ? 'text-emerald-700 font-semibold' : 'text-zinc-400'}`}>&euro;{allocated.toFixed(2)}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${st.cls}`} data-testid={`ic-status-${r.row_id}`}>{st.label}</span>
                            </td>
                            <td className="px-3 py-2 capitalize">{r.method || '—'}</td>
                            <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-0.5" data-testid={`ic-actions-${r.row_id}`}>
                                <button onClick={() => editInternalCashRow(r)} className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-sm transition-colors" title={t('edit') || 'Edit'} data-testid={`ic-edit-${r.row_id}`}><PencilSimple size={14} /></button>
                                <button onClick={() => setIcDeleteTarget(r)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors" title={t('delete') || 'Delete'} data-testid={`ic-delete-${r.row_id}`}><Trash size={14} /></button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })()}

        {/* STATEMENT */}
        {activeTab === 'statement' && (
          <div data-testid="sales-statement">
            <div className="flex items-center gap-3 mb-4">
              <Label className="text-xs">{t('client') || 'Client'}:</Label>
              <Select value={stmtClientId || ''} onValueChange={setStmtClientId}>
                <SelectTrigger className="rounded-none h-9 w-72" data-testid="sales-stmt-client"><SelectValue placeholder={t('selectClient') || 'Select client'} /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {statement && (
              <>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-white border border-zinc-200 p-4"><p className="text-xs text-zinc-500">{t('totalInvoiced') || 'Invoiced'}</p><p className="text-lg font-mono font-bold mt-1">{fmtMoney(statement.total_invoiced)}</p></div>
                  <div className="bg-white border border-emerald-200 p-4"><p className="text-xs text-emerald-700">{t('totalPayments') || 'Payments'}</p><p className="text-lg font-mono font-bold text-emerald-700 mt-1">{fmtMoney(statement.total_payments)}</p></div>
                  <div className={`bg-white border p-4 ${statement.balance > 0 ? 'border-red-200' : 'border-emerald-200'}`}><p className="text-xs text-zinc-500">{t('balance') || 'Balance'}</p><p className={`text-lg font-mono font-bold mt-1 ${statement.balance > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{fmtMoney(statement.balance)}</p></div>
                </div>
                <div className="bg-white border border-zinc-200">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-zinc-50 border-b text-xs text-zinc-500 uppercase">
                      <th className="text-left px-4 py-2.5">{t('date')}</th>
                      <th className="text-left px-4 py-2.5">{t('reference') || 'Reference'}</th>
                      <th className="text-left px-4 py-2.5">{t('description')}</th>
                      <th className="text-right px-4 py-2.5">{t('charge') || 'Charge'}</th>
                      <th className="text-right px-4 py-2.5">{t('payment')}</th>
                    </tr></thead>
                    <tbody>
                      {statement.entries.map((e, i) => (
                        <tr key={i} className="border-b border-zinc-100">
                          <td className="px-4 py-2 font-mono text-xs">{fmtDate(e.date)}</td>
                          <td className="px-4 py-2 font-mono text-xs">{e.reference}</td>
                          <td className="px-4 py-2 text-zinc-700">{e.description}</td>
                          <td className="px-4 py-2 text-right font-mono text-red-700">{e.type === 'invoice' ? fmtMoney(e.amount) : ''}</td>
                          <td className="px-4 py-2 text-right font-mono text-emerald-700">{e.type === 'payment' ? fmtMoney(e.amount) : ''}</td>
                        </tr>
                      ))}
                      {statement.entries.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-zinc-400">{t('noData') || 'No entries'}</td></tr>}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {!statement && <p className="text-zinc-400 text-sm">{t('selectClientToShowStatement') || 'Select a client to view their statement.'}</p>}
          </div>
        )}

        {/* CUSTOMER EDIT DIALOG */}
        <Dialog open={customerDialog} onOpenChange={setCustomerDialog}>
          <DialogContent className="rounded-none max-w-lg" data-testid="sales-customer-dialog">
            <DialogHeader><DialogTitle>{customerForm.name}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>TIN</Label><Input value={customerForm.tin} onChange={e => setCustomerForm(f => ({ ...f, tin: e.target.value }))} className="rounded-none mt-1" data-testid="sales-customer-tin" /></div>
              <div><Label>{t('email')}</Label><Input value={customerForm.email} onChange={e => setCustomerForm(f => ({ ...f, email: e.target.value }))} className="rounded-none mt-1" data-testid="sales-customer-email" /></div>
              <div><Label>{t('billingAddress') || 'Billing address'}</Label><Input value={customerForm.billing_address} onChange={e => setCustomerForm(f => ({ ...f, billing_address: e.target.value }))} className="rounded-none mt-1" data-testid="sales-customer-billing" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setCustomerDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none" onClick={saveCustomer} data-testid="sales-customer-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* INVOICE DIALOG */}
        <Dialog open={invoiceDialog} onOpenChange={(open) => {
          // Phase 601 — Closing the dialog (Esc / outside click / X) must
          // ALWAYS discard the clone draft. No phantom invoices are ever
          // created — `cloneInvoice` never calls POST; only the explicit
          // Save button hits the backend.
          if (!open) {
            setCloneSource(null);
          }
          setInvoiceDialog(open);
        }}>
          <DialogContent className="rounded-none sm:max-w-[1100px] max-h-[90vh] overflow-y-auto" data-testid="sales-invoice-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap">
                <span>
                  {editingInvoice
                    ? (t('editInvoice') || 'Edit Invoice')
                    : cloneSource
                      ? (t('newInvoiceCloneTitle') || 'New Invoice (Clone Draft)')
                      : (t('newInvoice') || 'New Invoice')}
                </span>
                {cloneSource && (
                  <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-300" data-testid="sales-inv-clone-draft-badge">
                    {t('draftNotSaved') || 'DRAFT — NOT SAVED'}
                  </span>
                )}
              </DialogTitle>
            </DialogHeader>
            {cloneSource && (
              <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 flex items-start gap-2" data-testid="sales-inv-clone-notice">
                <span className="font-bold">⚠</span>
                <div>
                  <div className="font-semibold">
                    {(t('clonedFromBanner') || 'Cloned from {n} — review the fields and click Save to create a new invoice.').replace('{n}', cloneSource.invoice_no || `#${(cloneSource.invoice_id || '').slice(-6)}`)}
                  </div>
                  <div className="mt-0.5 text-amber-700">
                    {t('clonedFromBannerHint') || 'Nothing has been saved yet. Close to discard — no invoice will be created until you click Save.'}
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-5">
              {/* Phase 431 — Invoice For toggle: Client OR Building */}
              <div className="flex items-center gap-4" data-testid="sales-inv-invoiced-to">
                <Label className="text-xs text-zinc-500 uppercase tracking-wide">{t('invoicedTo') || 'Invoice For'}:</Label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="sales-inv-target-client">
                  <input
                    type="radio"
                    name="invoiced_to"
                    checked={invoiceForm.invoiced_to === 'client'}
                    onChange={() => setInvoiceForm(f => ({ ...f, invoiced_to: 'client', building_id: '' }))}
                  />
                  {t('client') || 'Client'}
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer" data-testid="sales-inv-target-building">
                  <input
                    type="radio"
                    name="invoiced_to"
                    checked={invoiceForm.invoiced_to === 'building'}
                    onChange={() => setInvoiceForm(f => ({ ...f, invoiced_to: 'building', client_id: '' }))}
                  />
                  {t('building') || 'Building'}
                </label>
              </div>

              {/* Phase 518 — Invoice Type segmented control.
                  OFFICIAL → counts in Finance + VAT, uses SI-… numbering.
                  INTERNAL CASH → kept out of Finance/VAT, uses IC-… numbering.
                  Default: OFFICIAL (no behavioural change for existing
                  flows). Visible badge mirrors what shows in the list and
                  PDF so users immediately know what they're issuing. */}
              <div className="flex items-center gap-3" data-testid="sales-inv-type-toggle">
                <Label className="text-xs text-zinc-500 uppercase tracking-wide">{t('invoiceType') || 'Type'}:</Label>
                <button
                  type="button"
                  onClick={() => setInvoiceForm(f => ({ ...f, invoice_type: 'official_invoice' }))}
                  className={`text-xs px-3 py-1.5 font-semibold uppercase tracking-wider border rounded-none transition-colors ${invoiceForm.invoice_type === 'official_invoice' ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'}`}
                  data-testid="sales-inv-type-official"
                >
                  {t('invoiceTypeOfficial') || 'Official'}
                </button>
                <button
                  type="button"
                  onClick={() => setInvoiceForm(f => ({ ...f, invoice_type: 'internal_cash_tracking' }))}
                  className={`text-xs px-3 py-1.5 font-semibold uppercase tracking-wider border rounded-none transition-colors ${invoiceForm.invoice_type === 'internal_cash_tracking' ? 'bg-amber-500 text-white border-amber-600' : 'bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50'}`}
                  data-testid="sales-inv-type-internal"
                >
                  {t('invoiceTypeInternalCash') || 'Internal Cash'}
                </button>
                {invoiceForm.invoice_type === 'internal_cash_tracking' && (
                  <span className="text-[11px] text-amber-700 ml-2 italic">
                    {t('internalCashHint') || 'Will NOT appear in Finance / VAT / official exports.'}
                  </span>
                )}
              </div>

              {/* Top section — Client OR Building (mutually exclusive) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {invoiceForm.invoiced_to === 'client' ? (
                  <div>
                    <Label className="text-xs text-zinc-500">{t('client') || 'Client'} *</Label>
                    <Select value={invoiceForm.client_id} onValueChange={v => setInvoiceForm(f => ({ ...f, client_id: v }))}>
                      <SelectTrigger className="rounded-none mt-1 h-9" data-testid="sales-inv-client"><SelectValue placeholder={t('selectClient') || 'Select client'} /></SelectTrigger>
                      <SelectContent>
                        {customers.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label className="text-xs text-zinc-500">{t('building') || 'Building'} *</Label>
                    <Select value={invoiceForm.building_id} onValueChange={v => setInvoiceForm(f => ({ ...f, building_id: v }))}>
                      <SelectTrigger className="rounded-none mt-1 h-9" data-testid="sales-inv-building"><SelectValue placeholder={t('selectBuilding') || 'Select building'} /></SelectTrigger>
                      <SelectContent>
                        {buildings.map(b => <SelectItem key={b.building_id} value={b.building_id}>{b.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceNumber') || 'Invoice #'}</Label>
                  <Input
                    value={invoiceForm.invoice_no}
                    onChange={e => setInvoiceForm(f => ({ ...f, invoice_no: e.target.value }))}
                    placeholder={editingInvoice ? '' : (t('autoGenerated') || 'Auto-generated if left blank')}
                    className="rounded-none mt-1 font-mono h-9"
                    data-testid="sales-inv-number"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('referenceNumber') || 'Reference #'}</Label>
                  <Input
                    value={invoiceForm.reference_no}
                    onChange={e => setInvoiceForm(f => ({ ...f, reference_no: e.target.value }))}
                    placeholder="RF-2026-00125"
                    className="rounded-none mt-1 font-mono h-9"
                    data-testid="sales-inv-reference"
                  />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('invoiceDate') || t('date')}</Label>
                  <Input type="date" value={invoiceForm.date} onChange={e => setInvoiceForm(f => ({ ...f, date: e.target.value }))} className="rounded-none mt-1 font-mono h-9" data-testid="sales-inv-date" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('terms') || 'Terms'}</Label>
                  <Select value={invoiceForm.terms || '__none__'} onValueChange={(v) => {
                    const term = v === '__none__' ? '' : v;
                    const days = { net15: 15, net30: 30, net45: 45, net60: 60 }[term];
                    setInvoiceForm(f => {
                      const next = { ...f, terms: term };
                      if (days && f.date) {
                        const dt = new Date(f.date);
                        dt.setDate(dt.getDate() + days);
                        next.due_date = dt.toISOString().slice(0, 10);
                      }
                      return next;
                    });
                  }}>
                    <SelectTrigger className="rounded-none mt-1 h-9" data-testid="sales-inv-terms"><SelectValue placeholder={t('selectTerms') || 'Select'} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('custom') || 'Custom'}</SelectItem>
                      <SelectItem value="dueOnReceipt">{t('dueOnReceipt') || 'Due on Receipt'}</SelectItem>
                      <SelectItem value="net15">Net 15</SelectItem>
                      <SelectItem value="net30">Net 30</SelectItem>
                      <SelectItem value="net45">Net 45</SelectItem>
                      <SelectItem value="net60">Net 60</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">{t('dueDate') || 'Due date'}</Label>
                  <Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm(f => ({ ...f, due_date: e.target.value, terms: '' }))} className="rounded-none mt-1 font-mono h-9" data-testid="sales-inv-duedate" />
                </div>
              </div>

              {/* Invoice items — Phase 546: + Add Line moved BELOW the
                  last row (accounting-editor style), and a "#" numbering
                  column added on the far left so multi-row invoices are
                  easier to scan. */}
              <div className="border border-zinc-200" data-testid="sales-inv-items">
                <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wider text-zinc-700">{t('invoiceItems') || 'Invoice Items'}</span>
                  <span className="text-[10px] text-zinc-400 font-mono">{(invoiceForm.items || []).length} {t('lines') || 'lines'}</span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50/50">
                      <th className="px-2 py-2 text-center text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[32px]">#</th>
                      <th className="px-3 py-2 text-left text-[10px] text-zinc-500 font-semibold uppercase tracking-wide">{t('itemDescription') || 'Item Description'}</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[80px]">{t('quantity') || 'Qty'}</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[110px]">{t('rate') || 'Rate'} (€)</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[80px]">{t('vatRate') || 'VAT'} %</th>
                      <th className="px-2 py-2 text-right text-[10px] text-zinc-500 font-semibold uppercase tracking-wide w-[110px]">{t('amount') || 'Amount'}</th>
                      <th className="w-[36px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoiceForm.items || []).map((it, idx) => {
                      const qty = parseFloat(it.quantity) || 0;
                      const rate = parseFloat(it.rate) || 0;
                      const lineNet = Math.round(((qty * rate) + Number.EPSILON) * 100) / 100;
                      return (
                        <tr key={idx} className="border-b border-zinc-100" data-testid={`sales-inv-line-${idx}`}>
                          <td className="px-2 py-1.5 text-center font-mono text-[11px] font-semibold text-zinc-500 tabular-nums" data-testid={`sales-inv-line-num-${idx}`}>{idx + 1}</td>
                          <td className="px-2 py-1.5">
                            <Input value={it.description} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], description: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 border-zinc-200" placeholder={t('itemDescription') || 'Description'} data-testid={`sales-inv-line-desc-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.quantity} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], quantity: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`sales-inv-line-qty-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.01" min="0" value={it.rate} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], rate: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`sales-inv-line-rate-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5">
                            <Input type="number" step="0.5" min="0" max="100" value={it.vat_rate} onChange={e => setInvoiceForm(f => { const u = [...f.items]; u[idx] = { ...u[idx], vat_rate: e.target.value }; return { ...f, items: u }; })} className="rounded-none text-xs h-8 font-mono text-right border-zinc-200" data-testid={`sales-inv-line-vat-${idx}`} />
                          </td>
                          <td className="px-2 py-1.5 text-right font-mono text-xs font-semibold text-zinc-800" data-testid={`sales-inv-line-amount-${idx}`}>&euro;{lineNet.toFixed(2)}</td>
                          <td className="px-1 py-1.5 text-center">
                            <button type="button" onClick={() => setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))} className="p-1 text-zinc-400 hover:text-red-500" data-testid={`sales-inv-line-remove-${idx}`}>✕</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {/* Phase 546 — Add Line moved below the last row to mimic
                    accounting editors (Xero / QuickBooks / Zoho Books). */}
                <div className="border-t border-zinc-200 bg-zinc-50/40 px-3 py-2">
                  <button
                    type="button"
                    onClick={() => setInvoiceForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, rate: '', vat_rate: 19 }] }))}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium inline-flex items-center gap-1"
                    data-testid="sales-inv-add-line"
                  >
                    <Plus size={12} weight="bold" />
                    {t('addLine') || 'Add Line'}
                  </button>
                </div>
              </div>

              {/* Bottom totals — Phase 431/486: Subtotal / Discount / Tax Summary / Grand Total / Paid / Balance Due */}
              {(() => {
                // Phase 555 — Frontend totals use the SAME per-line round
                // strategy as the backend (`_money` with ROUND_HALF_UP).
                // Without this the preview can read €15.00 while the
                // backend stores €14.99 (or vice-versa). The single source
                // of truth for the stored values is still the backend, but
                // displaying matched numbers prevents user confusion.
                const r2 = (x) => {
                  const n = Number(x) || 0;
                  // round-half-up: shift, Math.round, shift back.
                  return Math.round((n + Number.EPSILON) * 100) / 100;
                };
                const items = invoiceForm.items || [];
                const lineNets = items.map(it => r2((parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0)));
                // Per-line VAT kept for display only.
                const lineVats = items.map((it, i) => r2(lineNets[i] * (parseFloat(it.vat_rate) || 0) / 100));
                const subtotal = r2(lineNets.reduce((s, n) => s + n, 0));
                // Phase 565 — Aggregate VAT by rate group (sum nets per
                // rate, then apply rate once, then round) to match the
                // backend and eliminate the €0.01 drift visible at
                // €203.38 × 19% (was €242.03, now €242.02).
                const vatByRate = items.reduce((acc, it, i) => {
                  const r = parseFloat(it.vat_rate) || 0;
                  acc[r] = (acc[r] || 0) + lineNets[i];
                  return acc;
                }, {});
                const rawVat = r2(
                  Object.entries(vatByRate).reduce((s, [r, net]) => s + net * (parseFloat(r) || 0) / 100, 0)
                );
                const discount = r2(Math.max(0, parseFloat(invoiceForm.discount) || 0));
                const adjustment = r2(parseFloat(invoiceForm.adjustment) || 0);
                const ratio = subtotal > 0 ? (subtotal - discount) / subtotal : 0;
                const vatTotal = r2(rawVat * ratio);
                const grand = r2((subtotal - discount) + vatTotal + adjustment);
                const paidAmount = r2(parseFloat(editingInvoice?.paid_amount || 0) || 0);
                const balance = r2(Math.max(0, grand - paidAmount));
                // Phase 486 — Group items by VAT rate so we can render a
                // per-rate Tax Summary identical to the one on the PDF.
                const taxBuckets = items.reduce((acc, it) => {
                  const v = parseFloat(it.vat_rate) || 0;
                  const net = (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0) * ratio;
                  const tax = net * v / 100;
                  acc[v] = acc[v] || { taxable: 0, tax: 0 };
                  acc[v].taxable += net;
                  acc[v].tax += tax;
                  return acc;
                }, {});
                const taxRates = Object.keys(taxBuckets).map(parseFloat).sort((a, b) => a - b);
                return (
                  <div className="flex flex-col md:flex-row gap-4 justify-end">
                    {/* Tax Summary (left) — matches the PDF layout */}
                    {taxRates.length > 0 && (
                      <div className="w-full md:w-[300px] border border-zinc-200 bg-white p-3 text-xs" data-testid="sales-inv-tax-summary">
                        <div className="font-bold uppercase tracking-wider text-zinc-700 mb-2 pb-1.5 border-b">{t('taxSummary') || 'Tax Summary'}</div>
                        <table className="w-full">
                          <thead>
                            <tr className="text-[10px] text-zinc-400 uppercase tracking-wider">
                              <th className="text-left font-medium pb-1">{t('rate') || 'Rate'}</th>
                              <th className="text-right font-medium pb-1">{t('taxableAmount') || 'Taxable'}</th>
                              <th className="text-right font-medium pb-1">{t('taxAmount') || 'Tax'}</th>
                              <th className="text-right font-medium pb-1">{t('total') || 'Total'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {taxRates.map(r => (
                              <tr key={r} className="border-t border-zinc-100">
                                <td className="py-1 font-mono">{r.toFixed(1)}%</td>
                                <td className="py-1 text-right font-mono">€{taxBuckets[r].taxable.toFixed(2)}</td>
                                <td className="py-1 text-right font-mono">€{taxBuckets[r].tax.toFixed(2)}</td>
                                <td className="py-1 text-right font-mono font-semibold">€{(taxBuckets[r].taxable + taxBuckets[r].tax).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="w-full md:w-[380px] border border-zinc-200 bg-zinc-50/50 p-3 space-y-1.5 text-xs" data-testid="sales-inv-totals">
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('subtotal') || 'Subtotal'}</span>
                        <span className="font-mono font-semibold text-zinc-800" data-testid="sales-inv-subtotal" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{subtotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-zinc-500 shrink-0">{t('discount') || 'Discount'}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400">&euro;</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={invoiceForm.discount}
                            onChange={e => setInvoiceForm(f => ({ ...f, discount: e.target.value }))}
                            className="rounded-none h-6 w-24 text-right font-mono text-xs border-zinc-300"
                            data-testid="sales-inv-discount-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('taxableAmount') || 'Taxable Amount'}</span>
                        <span className="font-mono text-zinc-700" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{(subtotal - discount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-zinc-500">{t('vatAmount') || 'VAT Amount'}</span>
                        <span className="font-mono text-zinc-700" data-testid="sales-inv-vat-total" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{vatTotal.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-zinc-500 shrink-0">{t('adjustment') || 'Adjustment'}</span>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400">&euro;</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={invoiceForm.adjustment}
                            onChange={e => setInvoiceForm(f => ({ ...f, adjustment: e.target.value }))}
                            className="rounded-none h-6 w-24 text-right font-mono text-xs border-zinc-300"
                            data-testid="sales-inv-adjustment-input"
                          />
                        </div>
                      </div>
                      <div className="flex justify-between border-t-2 border-zinc-900 pt-2 mt-2">
                        <span className="font-bold text-zinc-900 uppercase tracking-wider">{t('total') || 'Total'}</span>
                        <span className="font-mono font-bold text-base text-zinc-900" data-testid="sales-inv-grand-total" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{grand.toFixed(2)}</span>
                      </div>
                      {editingInvoice && paidAmount > 0 && (
                        <>
                          <div className="flex justify-between items-center pt-1">
                            <span className="text-emerald-700">{t('paid') || 'Paid'}</span>
                            <span className="font-mono text-emerald-700" data-testid="sales-inv-paid" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{paidAmount.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between items-center bg-amber-50 -mx-3 px-3 py-2 mt-1 border-y border-amber-200">
                            <span className="font-bold text-amber-900 uppercase tracking-wider">{t('balanceDue') || 'Balance Due'}</span>
                            <span className="font-mono font-bold text-base text-amber-900" data-testid="sales-inv-balance-due" style={{ fontVariantNumeric: 'tabular-nums' }}>&euro;{balance.toFixed(2)}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <Label className="text-xs text-zinc-500">{t('customerNotes') || 'Customer Notes'} <span className="text-zinc-400 font-normal">({t('visibleOnPdf') || 'visible on PDF'})</span></Label>
                <textarea
                  value={invoiceForm.customer_notes || ''}
                  onChange={e => setInvoiceForm(f => ({ ...f, customer_notes: e.target.value }))}
                  rows={2}
                  className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm"
                  placeholder={t('optional') || 'Optional — shown to customer'}
                  data-testid="sales-inv-customer-notes"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('internalNotes') || 'Internal Notes'} <span className="text-zinc-400 font-normal">({t('teamOnlyNotOnPdf') || 'team-only, NOT on PDF'})</span></Label>
                <textarea
                  value={invoiceForm.internal_notes || ''}
                  onChange={e => setInvoiceForm(f => ({ ...f, internal_notes: e.target.value }))}
                  rows={2}
                  className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm bg-amber-50/30"
                  placeholder={t('optional') || 'Optional — team only'}
                  data-testid="sales-inv-internal-notes"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">{t('termsAndConditions') || 'Terms & Conditions'} <span className="text-zinc-400 font-normal">({t('visibleOnPdf') || 'visible on PDF'})</span></Label>
                <textarea
                  value={invoiceForm.terms_and_conditions || ''}
                  onChange={e => setInvoiceForm(f => ({ ...f, terms_and_conditions: e.target.value }))}
                  rows={3}
                  className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm"
                  placeholder={t('termsPlaceholder') || 'Payment is due within 30 days. Late payments may incur interest. Goods remain property of seller until payment in full.'}
                  data-testid="sales-inv-terms-conditions"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setInvoiceDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none bg-zinc-950" onClick={saveInvoice} data-testid="sales-inv-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <InvoicePaymentsDialog
          invoice={paymentsDialogInv}
          open={!!paymentsDialogInv}
          onOpenChange={(o) => !o && setPaymentsDialogInv(null)}
          isAdmin={isAdmin}
          onChanged={() => { fetchInvoices(); fetchPayments(); fetchFinanceIncome(); }}
          onDeleteInvoice={(inv) => { setPaymentsDialogInv(null); setInvoiceDeleteTarget(inv); }}
        />

        {/* DELETE INVOICE CONFIRM — Phase 418: Capacitor-safe (no window.confirm) */}
        <AlertDialog open={!!invoiceDeleteTarget} onOpenChange={(o) => !o && setInvoiceDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="sales-inv-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deleteInvoice') || 'Delete Invoice'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeleteInvoice') || 'Are you sure you want to delete invoice'} <strong>{invoiceDeleteTarget?.invoice_no}</strong>? {t('thisCannotBeUndone') || 'This action cannot be undone.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="sales-inv-delete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction onClick={() => deleteInvoice(invoiceDeleteTarget)} className="rounded-none bg-red-600 hover:bg-red-700" data-testid="sales-inv-delete-confirm">{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* DELETE PAYMENT CONFIRM — Phase 540: Capacitor-safe replacement
            for window.confirm. Covers both the Customer Payments and the
            embedded payments list. */}
        <AlertDialog open={!!paymentDeleteTarget} onOpenChange={(o) => !o && setPaymentDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="sales-pay-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment') || 'Delete Payment'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeletePayment') || 'Delete this payment? Allocations on linked invoices will be reversed.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="sales-pay-delete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => { const tgt = paymentDeleteTarget; setPaymentDeleteTarget(null); if (tgt) await deletePayment(tgt); }}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="sales-pay-delete-confirm"
              >{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* VOID PAYMENT DIALOG — Phase 543: Safe cancel for bank-linked
            customer payments. Reverses allocations server-side and stamps
            an audit-log entry so accounting integrity stays intact. */}
        <Dialog open={!!paymentVoidTarget} onOpenChange={(o) => { if (!o) { setPaymentVoidTarget(null); setPaymentVoidReason(''); } }}>
          <DialogContent className="rounded-none max-w-lg" data-testid="sales-pay-void-dialog">
            <DialogHeader>
              <DialogTitle>{t('voidPayment') || 'Void Payment'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-zinc-600">
                {t('voidPaymentHint') || 'This payment is linked to a bank account, so it cannot be deleted. Voiding it will reverse every invoice allocation and preserve the original record in the audit log.'}
              </p>
              {paymentVoidTarget && (
                <div className="border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-mono" data-testid="sales-pay-void-summary">
                  <div><span className="text-zinc-500">{t('reference') || 'Reference'}:</span> {paymentVoidTarget.reference || paymentVoidTarget.sales_payment_id}</div>
                  <div><span className="text-zinc-500">{t('amount') || 'Amount'}:</span> {fmtMoney(paymentVoidTarget.amount)}</div>
                  <div><span className="text-zinc-500">{t('date') || 'Date'}:</span> {fmtDate(paymentVoidTarget.date)}</div>
                </div>
              )}
              <div>
                <Label className="text-xs">{t('reason') || 'Reason'}</Label>
                <Input
                  value={paymentVoidReason}
                  onChange={(e) => setPaymentVoidReason(e.target.value)}
                  className="rounded-none mt-1"
                  placeholder={t('voidReasonPlaceholder') || 'Why is this payment being voided?'}
                  data-testid="sales-pay-void-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => { setPaymentVoidTarget(null); setPaymentVoidReason(''); }} data-testid="sales-pay-void-cancel">{t('cancel') || 'Cancel'}</Button>
              <Button
                className="rounded-none bg-red-600 hover:bg-red-700 text-white"
                onClick={async () => { const tgt = paymentVoidTarget; const rsn = paymentVoidReason; setPaymentVoidTarget(null); setPaymentVoidReason(''); if (tgt) await voidPayment(tgt, rsn); }}
                data-testid="sales-pay-void-confirm"
              >{t('void') || 'Void Payment'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* DELETE INTERNAL CASH ROW CONFIRM — Phase 540: handles both
            sales_payment and finance_income rows in the Internal Cash
            customer-centric ledger. */}
        <AlertDialog open={!!icDeleteTarget} onOpenChange={(o) => !o && setIcDeleteTarget(null)}>
          <AlertDialogContent className="rounded-none" data-testid="ic-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePayment') || 'Delete Entry'}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('confirmDeleteInternalCashRow') || 'Delete this Internal Cash entry? Any allocations on linked Internal Invoices will be reversed.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-none" data-testid="ic-delete-cancel">{t('cancel') || 'Cancel'}</AlertDialogCancel>
              <AlertDialogAction
                onClick={async () => { const tgt = icDeleteTarget; setIcDeleteTarget(null); if (tgt) await deleteInternalCashRow(tgt); }}
                className="rounded-none bg-red-600 hover:bg-red-700"
                data-testid="ic-delete-confirm"
              >{t('delete') || 'Delete'}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* SEND-EMAIL DIALOG — Phase 418 */}
        <Dialog open={!!invoiceEmailTarget} onOpenChange={(o) => !o && setInvoiceEmailTarget(null)}>
          <DialogContent className="rounded-none max-w-xl" data-testid="sales-inv-email-dialog">
            <DialogHeader>
              <DialogTitle>{t('sendByEmail') || 'Send by Email'} — {invoiceEmailTarget?.invoice_no}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs">{t('to') || 'To'} *</Label>
                <Input value={invoiceEmailForm.to} onChange={e => setInvoiceEmailForm(f => ({ ...f, to: e.target.value }))} className="rounded-none mt-1" placeholder="customer@example.com" data-testid="sales-inv-email-to" />
              </div>
              <div>
                <Label className="text-xs">CC</Label>
                <Input value={invoiceEmailForm.cc} onChange={e => setInvoiceEmailForm(f => ({ ...f, cc: e.target.value }))} className="rounded-none mt-1" placeholder={t('optional') || 'Optional, comma-separated'} data-testid="sales-inv-email-cc" />
              </div>
              <div>
                <Label className="text-xs">{t('subject') || 'Subject'}</Label>
                <Input value={invoiceEmailForm.subject} onChange={e => setInvoiceEmailForm(f => ({ ...f, subject: e.target.value }))} className="rounded-none mt-1" data-testid="sales-inv-email-subject" />
              </div>
              <div>
                <Label className="text-xs">{t('message') || 'Message'}</Label>
                <textarea value={invoiceEmailForm.message} onChange={e => setInvoiceEmailForm(f => ({ ...f, message: e.target.value }))} rows={6} className="w-full mt-1 border border-zinc-200 rounded-none px-2 py-1.5 text-sm" data-testid="sales-inv-email-message" />
              </div>
              <p className="text-[11px] text-zinc-500">{t('invoicePdfAttached') || 'The invoice PDF will be attached automatically.'}</p>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setInvoiceEmailTarget(null)} disabled={invoiceEmailSending}>{t('cancel') || 'Cancel'}</Button>
              <Button className="rounded-none bg-emerald-700 hover:bg-emerald-800" onClick={sendInvoiceEmail} disabled={invoiceEmailSending} data-testid="sales-inv-email-send">
                <Envelope size={14} className="mr-1.5" />
                {invoiceEmailSending ? (t('sending') || 'Sending…') : (t('send') || 'Send')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* PAYMENT DIALOG */}
        <Dialog open={paymentDialog} onOpenChange={(o) => { setPaymentDialog(o); if (!o) { setEditingPaymentId(null); setEditingIncomeId(null); } }}>
          <DialogContent className="rounded-none max-w-2xl" data-testid="sales-payment-dialog">
            <DialogHeader><DialogTitle>{(editingPaymentId || editingIncomeId) ? (t('editPayment') || 'Edit Payment') : (t('newPayment') || 'New Payment')}</DialogTitle></DialogHeader>
            {/* Phase 540 — When editing a Finance Income (Internal Cash)
                entry, only allocations are mutable; the income amount /
                date / method are owned by Finance → Income. Show a banner
                so the user knows. */}
            {editingIncomeId && (
              <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="sales-pay-income-banner">
                {t('editIncomeAllocOnly') || 'Editing a Finance Income entry — only invoice allocations are saved. Amount, date and method are managed in Finance → Income.'}
              </div>
            )}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('clientVendorBuilding') || 'Client / Vendor / Building'} *</Label>
                  {/* Phase 566 — Unified selector. Reuses the same options
                      and matcher used by Finance → Invoices so Buildings
                      appear as first-class entities here too. */}
                  <Select value={paymentForm._entity_value || paymentForm.client_id} onValueChange={onPaymentClientChange} disabled={!!editingIncomeId}>
                    <SelectTrigger className="rounded-none mt-1 disabled:bg-zinc-100 disabled:text-zinc-500" data-testid="sales-pay-client"><SelectValue placeholder={t('selectClientOrBuilding') || t('selectClient') || 'Select customer or building'} /></SelectTrigger>
                    <SelectContent className="max-h-80 overflow-y-auto">
                      {clientVendorBuildingOptions.clients.length > 0 && (
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-zinc-400">{t('clientsAndVendors') || 'Clients & Vendors'}</div>
                      )}
                      {clientVendorBuildingOptions.clients.map(c => (
                        <SelectItem key={c.client_id} value={c.client_id} data-testid={`sales-pay-opt-${c.client_id}`}>{c.name}</SelectItem>
                      ))}
                      {clientVendorBuildingOptions.buildings.length > 0 && (
                        <div className="px-2 py-1 mt-1 text-[10px] uppercase tracking-wider text-zinc-400 border-t border-zinc-100 pt-2">{t('buildings') || 'Buildings'}</div>
                      )}
                      {clientVendorBuildingOptions.buildings.map(b => (
                        <SelectItem key={b.value} value={b.value} data-testid={`sales-pay-opt-${b.value}`}>
                          <span className="inline-flex items-center gap-2">
                            <span>{b.name}</span>
                            <span className="text-[9px] uppercase tracking-wider px-1 py-0.5 bg-indigo-50 text-indigo-700 border border-indigo-200">{t('building') || 'Building'}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('bankAccount') || 'Bank account'}</Label>
                  <BankAccountCombobox
                    t={t}
                    bankAccounts={bankAccounts}
                    value={paymentForm.bank_account_id}
                    onChange={(id) => setPaymentForm(f => ({ ...f, bank_account_id: id }))}
                    onCreateBank={() => { setPaymentDialog(false); window.location.href = '/finance?tab=banks'; }}
                    testid="sales-pay-bank"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t('date')}</Label><Input type="date" value={paymentForm.date} onChange={e => setPaymentForm(f => ({ ...f, date: e.target.value }))} disabled={!!editingIncomeId} className="rounded-none mt-1 disabled:bg-zinc-100 disabled:text-zinc-500" data-testid="sales-pay-date" /></div>
                <div><Label>{t('amount') || 'Amount'} *</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} disabled={!!editingIncomeId} className="rounded-none mt-1 disabled:bg-zinc-100 disabled:text-zinc-500" data-testid="sales-pay-amount" /></div>
                <div>
                  <Label>{t('method') || 'Method'}</Label>
                  <Select value={paymentForm.method} onValueChange={v => setPaymentForm(f => ({ ...f, method: v }))} disabled={!!editingIncomeId}>
                    <SelectTrigger className="rounded-none mt-1 disabled:bg-zinc-100 disabled:text-zinc-500"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t('cash') || 'Cash'}</SelectItem>
                      <SelectItem value="bank">{t('bankTransfer') || 'Bank transfer'}</SelectItem>
                      <SelectItem value="card">{t('card') || 'Card'}</SelectItem>
                      <SelectItem value="cheque">{t('cheque') || 'Cheque'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>{t('paymentNumber') || 'Payment Number'}</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))} placeholder={t('paymentNumberPlaceholder') || 'Auto-generated if left empty'} className="rounded-none mt-1" data-testid="sales-pay-reference" /></div>
                <div><Label>{t('notes') || 'Notes'}</Label><Input value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} className="rounded-none mt-1" /></div>
              </div>
              {/* Allocations — Phase 452: proper table with Invoice # / Date / Total / Outstanding / Allocate.
                  Phase 540 — Filter the visible invoice list by the
                  payment's account so Internal Cash payments only see
                  Internal Invoices, and Official payments only see
                  Official Invoices. Outstanding = total − paid_amount
                  AFTER reversing this payment's own current allocations
                  (so the displayed outstanding reflects what the user
                  still has to allocate, not a stale value). */}
              {paymentInvoices.length > 0 && (() => {
                const acct = paymentForm.payment_account || 'company_cash';
                const wantInternal = acct === 'internal_cash_tracking';
                // Reverse the current allocations of THIS payment when in
                // edit mode, so the row shows the true remaining balance.
                const myAllocByInv = {};
                (paymentForm.allocations || []).forEach(a => {
                  myAllocByInv[a.invoice_id] = (myAllocByInv[a.invoice_id] || 0) + (Number(a.amount) || 0);
                });
                const open = paymentInvoices
                  .filter(i => {
                    const ty = i.invoice_type || 'official_invoice';
                    // Phase 735.55 — Cross-type allocations. Internal Cash
                    // payments STILL only touch internal invoices (they
                    // must never affect the official ledger), but Official
                    // Bank payments can now allocate to BOTH official and
                    // internal invoices so operators can settle an
                    // Internal receivable that a customer paid via bank.
                    if (wantInternal && ty !== 'internal_cash_tracking') return false;
                    const adjustedPaid = Math.max(0, Number(i.paid_amount || 0) - (myAllocByInv[i.invoice_id] || 0));
                    const stillOpen = (i.status || '') !== 'paid' && (Number(i.total || 0) - adjustedPaid) > 0.005;
                    const hasOurAlloc = (myAllocByInv[i.invoice_id] || 0) > 0;
                    return stillOpen || hasOurAlloc;
                  })
                  .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
                const allocSum = (paymentForm.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                const amt = Number(paymentForm.amount) || 0;
                const unallocated = Math.max(0, amt - allocSum);
                return (
                  <div className="border border-zinc-200 p-3 bg-zinc-50" data-testid="sales-pay-allocations">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{t('matchPaymentToInvoice') || 'Allocate to invoices'} <span className="text-zinc-400 font-normal">({open.length} {t('openInvoices') || 'open'})</span></p>
                      <p className="text-[11px] text-zinc-500" data-testid="sales-pay-alloc-summary">
                        {t('allocated') || 'Allocated'}: <span className="font-mono font-semibold" data-testid="sales-pay-alloc-total">€{allocSum.toFixed(2)}</span>
                        {' · '}
                        {t('unallocated') || 'Unallocated'}: <span className={`font-mono font-semibold ${unallocated > 0.005 ? 'text-amber-700' : 'text-emerald-700'}`} data-testid="sales-pay-unallocated">€{unallocated.toFixed(2)}</span>
                      </p>
                    </div>
                    {/* Phase 540 — Quick "Allocate full payment to oldest invoices" button */}
                    {open.length > 0 && unallocated > 0.005 && (
                      <div className="mb-2 flex justify-end">
                        <button
                          type="button"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                          data-testid="sales-pay-alloc-auto"
                          onClick={() => {
                            // Auto-allocate FIFO across open invoices using
                            // the remaining unallocated amount.
                            let remaining = unallocated;
                            setPaymentForm(f => {
                              const next = [...(f.allocations || [])];
                              for (const inv of open) {
                                if (remaining <= 0.005) break;
                                const adjustedPaid = Math.max(0, Number(inv.paid_amount || 0) - (myAllocByInv[inv.invoice_id] || 0));
                                const outstanding = Math.max(0, Number(inv.total || 0) - adjustedPaid);
                                const already = (myAllocByInv[inv.invoice_id] || 0);
                                const room = Math.max(0, outstanding - already);
                                if (room <= 0.005) continue;
                                const take = Math.min(remaining, room);
                                const existingIdx = next.findIndex(a => a.invoice_id === inv.invoice_id);
                                if (existingIdx >= 0) next[existingIdx] = { ...next[existingIdx], amount: Number((next[existingIdx].amount || 0) + take).toFixed(2) * 1 };
                                else next.push({ invoice_id: inv.invoice_id, amount: Number(take.toFixed(2)) });
                                remaining -= take;
                              }
                              return { ...f, allocations: next };
                            });
                          }}
                        >{t('autoAllocateFifo') || 'Auto-allocate (oldest first)'}</button>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto bg-white border border-zinc-100">
                      <table className="w-full text-xs">
                        <thead className="bg-zinc-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold">#</th>
                            <th className="text-left px-2 py-1.5 font-semibold">{t('date') || 'Date'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('total') || 'Total'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold">{t('outstanding') || 'Outstanding'}</th>
                            <th className="text-right px-2 py-1.5 font-semibold w-32">{t('allocate') || 'Allocate'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {open.map(inv => {
                            const allocated = (paymentForm.allocations || []).find(a => a.invoice_id === inv.invoice_id)?.amount || '';
                            // Phase 540 — In edit mode the invoice's stored
                            // paid_amount still INCLUDES this payment's
                            // current allocations. Reverse them locally so
                            // the displayed outstanding represents what is
                            // truly available to allocate.
                            const mine = myAllocByInv[inv.invoice_id] || 0;
                            const adjustedPaid = Math.max(0, Number(inv.paid_amount || 0) - mine);
                            const outstanding = Math.max(0, Number(inv.total || 0) - adjustedPaid);
                            return (
                              <tr key={inv.invoice_id} className="border-t border-zinc-100" data-testid={`sales-pay-alloc-row-${inv.invoice_id}`}>
                                <td className="px-2 py-1.5 font-mono">{inv.invoice_no || '—'}</td>
                                <td className="px-2 py-1.5 font-mono text-zinc-500">{fmtDate(inv.date)}</td>
                                <td className="px-2 py-1.5 font-mono text-right">{fmtMoney(inv.total)}</td>
                                <td className="px-2 py-1.5 font-mono text-right text-red-700 font-semibold">{fmtMoney(outstanding)}</td>
                                <td className="px-2 py-1">
                                  <div className="flex items-center justify-end gap-1">
                                    <Input
                                      type="number"
                                      step="0.01"
                                      max={outstanding.toFixed(2)}
                                      min="0"
                                      value={allocated}
                                      onChange={e => setAllocation(inv.invoice_id, e.target.value)}
                                      className="rounded-none h-7 w-24 text-xs text-right font-mono"
                                      placeholder="0.00"
                                      data-testid={`sales-pay-alloc-${inv.invoice_id}`}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => setAllocation(inv.invoice_id, outstanding.toFixed(2))}
                                      className="text-[9px] text-blue-600 hover:underline whitespace-nowrap"
                                      title={t('allocateFull') || 'Allocate full outstanding'}
                                      data-testid={`sales-pay-alloc-full-${inv.invoice_id}`}
                                    >{t('full') || 'Full'}</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                          {open.length === 0 && (
                            <tr><td colSpan={5} className="text-center py-4 text-zinc-400">{t('noOpenInvoices') || 'No open invoices to allocate.'}</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => setPaymentDialog(false)}>{t('cancel')}</Button>
              <Button className="rounded-none" onClick={savePayment} data-testid="sales-pay-save">{t('save')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 726 — PDF Export Options dialog. Opens BEFORE generating
            the PDF, lets the user tick which columns to include, remembers
            the selection in localStorage. At least one column required. */}
        <Dialog open={pdfColsOpen} onOpenChange={setPdfColsOpen}>
          <DialogContent className="max-w-md rounded-none" data-testid="pdf-cols-dialog">
            <DialogHeader>
              <DialogTitle>{t('pdfExportOptions') || 'PDF Export Options'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <p className="text-xs text-zinc-500 mb-2">{t('pdfExportOptionsHint') || 'Select the columns you want to include in the PDF.'}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 max-h-96 overflow-y-auto">
                {ALL_PDF_COLS.map(col => {
                  const checked = pdfCols.includes(col.key);
                  return (
                    <label key={col.key} className="flex items-center gap-2 text-sm cursor-pointer select-none py-1 hover:bg-zinc-50 px-2" data-testid={`pdf-col-${col.key}`}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePdfCol(col.key)}
                        className="h-4 w-4 accent-zinc-900"
                        data-testid={`pdf-col-checkbox-${col.key}`}
                      />
                      <span className={checked ? 'text-zinc-900' : 'text-zinc-500'}>{col.label}</span>
                    </label>
                  );
                })}
              </div>
              {pdfCols.length === 0 && (
                <p className="text-xs text-red-600 mt-2" data-testid="pdf-cols-required">
                  {t('pdfExportAtLeastOne') || 'Please select at least one column.'}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-none" onClick={() => { setPdfColsOpen(false); setPendingPdfBuild(null); }} data-testid="pdf-cols-cancel">
                {t('cancel') || 'Cancel'}
              </Button>
              <Button className="rounded-none bg-zinc-950" disabled={pdfCols.length === 0} onClick={handleConfirmPdfCols} data-testid="pdf-cols-confirm">
                <FilePdf size={14} className="mr-1" />{t('viewPdf') || 'View PDF'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Phase 520/727 — Inline Invoice PDF preview modal. We fetch the
            PDF as a Blob and embed via `blob:` URL (same-origin,
            Cloudflare-agnostic) instead of pointing the iframe at the
            authenticated endpoint directly. This avoids Chrome's
            "This page has been blocked" error caused by interstitial
            bot-challenge / third-party cookie policies. Also exposes
            explicit "Open in new tab" and "Download" fallback buttons. */}
        <Dialog open={invoicePdfOpen} onOpenChange={(o) => {
          setInvoicePdfOpen(o);
          if (!o) { setInvoicePdfUrl(''); setInvoicePdfBlobUrl(''); setInvoicePdfError(''); }
        }}>
          <DialogContent className="max-w-5xl w-full h-[85vh] flex flex-col p-0" data-testid="sales-invoice-pdf-modal">
            <DialogHeader className="px-4 py-3 border-b border-zinc-200 flex flex-row items-center justify-between flex-shrink-0">
              <DialogTitle className="text-sm font-semibold truncate pr-4" data-testid="sales-invoice-pdf-title">
                {invoicePdfTitle || (t('invoice') || 'Invoice')}
              </DialogTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  className="rounded-none h-7 text-xs"
                  onClick={() => {
                    if (!invoiceEditing?.invoice_id && !invoiceViewing?.invoice_id) return;
                    const invId = invoiceViewing?.invoice_id || invoiceEditing?.invoice_id;
                    openPdfInBrowser(`/sales/invoices/${invId}/pdf`, { disposition: 'inline' })
                      .catch(e => toast.error(e?.message || 'Failed to open PDF'));
                  }}
                  disabled={!invoicePdfUrl}
                  data-testid="sales-invoice-pdf-open-tab"
                >
                  {t('openInNewTab') || 'Open in new tab'}
                </Button>
                <Button
                  variant="outline"
                  className="rounded-none h-7 text-xs"
                  onClick={() => {
                    if (!invoicePdfBlobUrl) return;
                    const a = document.createElement('a');
                    a.href = invoicePdfBlobUrl;
                    a.download = `${(invoicePdfTitle || 'invoice').replace(/[^a-z0-9-_ ]/gi, '_').trim() || 'invoice'}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }}
                  disabled={!invoicePdfBlobUrl}
                  data-testid="sales-invoice-pdf-download"
                >
                  <DownloadSimple size={14} className="mr-1" />
                  {t('download') || 'Download'}
                </Button>
              </div>
            </DialogHeader>
            <div className="flex-1 bg-zinc-100 overflow-hidden">
              {invoicePdfError ? (
                <div className="flex flex-col items-center justify-center h-full text-zinc-600 text-sm gap-3 p-6 text-center" data-testid="sales-invoice-pdf-error">
                  <p>{t('pdfEmbedBlocked') || 'The browser blocked the embedded PDF preview.'}</p>
                  <p className="text-xs text-zinc-500 max-w-md">{invoicePdfError}</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      className="rounded-none bg-zinc-950 h-8 text-xs"
                      onClick={() => {
                        // Extract path from the buildPdfUrl invoice URL and re-route through centralized helper.
                        try {
                          const u = new URL(invoicePdfUrl);
                          const path = u.pathname.replace(/^\/api/, '');
                          openPdfInBrowser(path, { disposition: 'inline' })
                            .catch(e => toast.error(e?.message || 'Failed to open PDF'));
                        } catch (_e) { /* noop */ }
                      }}
                      data-testid="sales-invoice-pdf-fallback-tab"
                    >
                      {t('openInNewTab') || 'Open in new tab'}
                    </Button>
                  </div>
                </div>
              ) : invoicePdfLoading ? (
                <div className="flex items-center justify-center h-full text-zinc-500 text-sm">{t('loading') || 'Loading…'}</div>
              ) : invoicePdfBlobUrl ? (
                <iframe
                  src={invoicePdfBlobUrl}
                  title={invoicePdfTitle}
                  className="w-full h-full border-0"
                  data-testid="sales-invoice-pdf-iframe"
                />
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 text-sm">{t('loading') || 'Loading…'}</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Container>
  );
}
